import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  each,
  isArray,
  keyBy,
  keys,
  mergeWith,
  sortBy,
  cloneDeep,
  isEqual,
} from "lodash";

import {
  ADD,
  ADD_REDIRECT,
  REPLACE,
  REMOVE,
  LEVEL_TO_POSTCODE_KEY,
  REMOVE_REDIRECT,
  REMOVE_TO_NONE,
} from "../../consts/map";
import {
  RESOURCE_GONE,
  DEPOT_MAP_CHANGED,
  MATRIX_TEMPLATE_CHANGED,
  DRIVER_PROFILE_CHANGED,
  DRIVER_DAY_CHANGED,
  ROUTE_DAY_CHANGED,
  DRIVER_PROFILE_LOCKED,
  DRIVER_DAY_LOCKED,
  MATRIX_TEMPLATE_USER_SESSION_CHANGED,
  ROUTE_DAY_LOCKED,
  ROUTE_DAY_LOCKED_IN_OTHER_BROWSER,
  DRIVER_DAY_LOCKED_IN_OTHER_BROWSER,
  DRIVER_PROFILE_LOCKED_FOR_PLAN_COPY,
  DRIVER_PROFILE_LOCKED_IN_OTHER_BROWSER,
} from "../../consts/errorCodes";
import { COLLECTION_TAB_FOR_TYPE } from "../../consts/collections";
import {
  BLANK,
  DATED,
  DEFAULT_TIMEBAND,
  PLAN_FAILED_TO_LOCK,
  PLAN_FAILED_TO_UNLOCK,
  DRIVER_FORCE_UNLOCK_FAILED,
  DRIVER_DAY_FAILED_TO_LOCK,
  DRIVER_DAY_FAILED_TO_UNLOCK,
  DRIVER_DAY_SUCESSFULLY_LOCKED,
  DRIVER_DAY_SUCESSFULLY_UNLOCKED,
  ROUTE_DAY_FAILED_TO_LOCK,
  ROUTE_DAY_FAILED_TO_UNLOCK,
  ROUTE_DAY_HAS_CHANGED,
  ROUTE_DAY_SUCESSFULLY_LOCKED,
  ROUTE_DAY_SUCESSFULLY_UNLOCKED,
  ERROR_PROCESSING_EDIT,
  MATRIX_DAY_MISSING,
  MATRIX_MODES,
  MATRIX_TYPE_MISSING,
  SAVE_PLAN_BEFORE_EDITING_DRIVER,
  TEMPLATE,
} from "../../consts/strings";
import {
  COPY_TO_DAILY_PLAN,
  CREATE_BLANK_TEMPLATE,
  CREATE_FROM_TEMPLATE,
  DELETE_MATRIX_TEMPLATE,
  GET_VEHILCES,
  GET_MATRIX_DAY,
  GET_DAY_SECTIONS,
  GET_MATRIX_PROFILES,
  GET_MATRIX_TEMPLATE,
  GET_SCHEDULED_COLLECTIONS,
  GET_SIMULATION_PERCENTAGES,
  GET_VOLUME_DATA,
  GET_ROBOT_SUPPLIERS,
  GET_VOLUME_OVERLAYS,
  POST_LOCK_TEMPLATE,
  POST_UNLOCK_TEMPLATE,
  SAVE_MATRIX_TEMPLATE,
  UPDATE_MATRIX_TEMPLATE_NAME,
  LOCK_DRIVER_DAY,
  UNLOCK_DRIVER_DAY,
  FORCE_UNLOCK_DEPOT_DRIVERS,
  FORCE_UNLOCK_DEPOT_DRIVERS_DAY,
  UPDATE_MATRIX_DAY,
  LOCK_ROUTE_DAY,
  UNLOCK_ROUTE_DAY,
  GET_MATRIX_DAY_LOCKS,
  GET_PICKUP_PARTNERS,
} from "../../consts/apis";
import {
  NEW_PLAN_UNALLOCATED_DRIVERS_ROUTES,
  ERROR_CREATE_PLAN,
  ERROR_DELETE_PLAN,
  ERROR_EDIT_PLAN,
  ERROR_FETCH_PROFILE,
  ERROR_FETCH_PROFILES,
  ERROR_FETCH_SIMULATION_PERCENTAGES,
  ERROR_FETCH_VOLUME_OVERLAY,
  ERROR_PLAN_NOT_VALIDATED,
  ERROR_SAVING_PLAN,
  ERROR_FETCH_ROBOT_SUPPLIERS,
  ERROR_FETCH_VOLUME_OVERLAYS,
  INVALID_DEPOT_OR_DATE,
  SUCCESS_COPY_PLAN,
  SUCCESS_CREATE_PLAN,
  SUCCESS_DELETE_PLAN,
  SUCCESS_EDIT_PLAN,
  SUCCESS_PLAN_SAVED,
  SUCCESS_LOCK_PLAN,
  SUCCESS_UNLOCK_PLAN,
  SUCCESS_UNLOCK_DEPOT_DRIVERS,
  SUCCESS_UNLOCK_MATRIX_DAY,
  SUCCESS_FETCH_PROFILES,
  SUCCESS_FETCH_PROFILE,
  SUCCESS_FETCH_VOLUME_OVERLAY,
  SUCCESS_FETCH_ROBOT_SUPPLIERS,
  SUCCESS_FETCH_VOLUME_OVERLAYS,
} from "../../consts/messages";
import {
  CHOICE_PLAN_TYPE,
  COLLECTIONS_PLAN_TYPE,
  DEFAULT_PLAN_TYPE,
  DELIVERY_PLAN_TYPES,
  LOCATIONS_PLAN_TYPE,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
  ROBOTS_PLAN_TYPE,
} from "../../consts/planTypes";
import { OUTER, POSTCODE, SECTOR } from "../../consts/layers";
import { DEFAULT_OVERRIDE } from "../../consts/overrides";
import { DEFAULT_LABEL_CHANGES } from "../../consts/routeDetails";

import {
  formatDateToCorrectTimeZone,
  formatDateToSQLTimestamp,
  offsetDate,
} from "../../utils/date";
import {
  generateRouteConfigFromRoutes,
  generateCollectionAreaConfigFromAreas,
  timeBandsSorter,
} from "../../utils/routeMatrix";
import {
  saveTemplateChecksum,
  saveRouteDayChecksum,
  saveDriverDayChecksum,
  processTemplateLocks,
  deleteTemplateChecksum,
  deleteDriverDayChecksum,
  deleteDepotDriverProfileChecksums,
  deleteDepotDriverDayChecksums,
  deleteDepotRouteDayChecksums,
  deleteRouteDayChecksum,
  processTemplateLocksRefresh,
} from "../../utils/locks";
import { getRedirectConfig } from "../../utils/redirectConfig";
import { applyWaveMove } from "../../utils/waves";
import {
  applyChangeToMatrixDay,
  generateDriverProfilesConfig,
  generateDriverDaysConfig,
  updateDriverDay,
} from "../../utils/drivers";
import { applyChangeToProfile } from "../../utils/matrixProfile";
import {
  handleCollectionTimeEdits,
  loadCollectionsType,
  applyChangeToCollections,
} from "../../utils/collections";
import {
  loadOverrideType,
  updateOverrideFromLevel,
  getOverridesWithConflicts,
} from "../../utils/overrides";
import { applyLocationChange, loadLocationsType } from "../../utils/locations";
import {
  getPickupModalConfig,
  getPickupRouteCodesMap,
  getPickupToSelectedPostcodesByType,
  getPickupUpdateOptions,
  getUpdatedPickupModal,
  loadPickupType,
  updatePickupAreas,
} from "../../utils/pickup";
import {
  generateRouteDaysConfig,
  processRoutesProfile,
  routeHasTerritory,
} from "../../utils/routes";
import { Postcode } from "../../utils/postcode";
import { findProfile } from "../../utils/plans";
import { isPlanTypeValid } from "../../utils/validation";
import { loadDefaultTypes } from "../../utils/defaultType";
import { optimiseRoute } from "../../utils/optimisation";
import { getOverridesRouteCodesMap } from "../../utils/overrides";
import { getRouteEditOptions } from "../../utils/routeEditOptions";
import { getCollectionEditAction } from "../../utils/collectionEditAction";
import { asyncLoop, serialAsyncForEach, syncToAsync } from "../../utils/async";

import api from "../../services/api";

import {
  addNotification,
  increaseDeterminateLoading,
  resetDeterminateLoading,
  resetSelectedPostcodes,
  setCollectionEditPostcode,
  setSelectedRoute,
  setCollectionTimeEdit,
  setCopyModalOpen,
  setCreateDatedPlanModalOpen,
  setCreateNewTemplateModalOpen,
  setDeterminateLoading,
  setEditPlanModalOpen,
  setSelectPlanModalOpen,
  setShowOptimiseAll,
  setEditOverridesModalOpen,
  setSelectSimulatedOverlayOpen,
  setSelectedSimulationPercentage,
  setDriversLockedError,
  setPlanManagementExpiredModalOpen,
  setShowReloadPlanManagement,
  setEditPickupModalOpen,
  setPickupEditOptionsModal,
} from "../ownApp";
import { setRouteWithTimeband } from "../ownApp/thunkActions";
import { fetchLookUpTables } from "../depot/thunkActions";
import {
  matrixProfileSelector,
  getMatrixProfileType,
  routeConfigEditSelector,
  routeConfigEditCopySelector,
  collectionAreasSelector,
  routeInEditSelector,
  routesSelector,
  editRoutesCopySelector,
  overrideAreasSelector,
  overridesForSelectedPostcodesSelector,
  editModalOverridesSelector,
  volumeDataSelector,
  areasWaveSplitSelector,
  redirectConfigSelector,
  redirectConfigCopySelector,
  routesTimesSelector,
  routesByOuterSelector,
  routesImpactedSelector,
  routesTimesCopySelector,
  cutOffTimesSelector,
  isProfileValidSelector,
  routeInEditCodeSelector,
  collectionsSelector,
  routeSelector,
  routeDayLockSelector,
  routeDayLocksSelector,
  driversSelector,
  driverDayLockSelector,
  driverDayLocksSelector,
  driverInEditSelector,
  driverInEditCopySelector,
  depotRoutesOptimisedSelector,
  vehiclesSelector,
  isDailyPlanSelector,
  driverlessRoutesSelector,
  driverSelector,
  driverProfileLockSelector,
  getSelectedPlanDay,
  getSelectedPlanDate,
  driversImpactedSelector,
  getSelectedMatrixProfile,
  getRouteMatrix,
  matrixModeSelector,
  isTemplateSelector,
  getMatrixProfiles,
  driverProfileLocksSelector,
  editRoutesSelector,
  matrixProfileLockSelector,
  matrixProfileChecksumSelector,
  matrixProfileSessionChecksumSelector,
  templateIdSelector,
  routesModifiedSelector,
  routesModifiedCopySelector,
  routeCodesImpactedSelector,
  userSessionExpiredSelector,
  locksRefreshRequiredSelector,
  editLocationConfigSelector,
  locationConfigSelector,
  locationSelector,
  pickupAreasSelector,
  pickupPartnersSelector,
  editModalPickupAreasSelector,
  pickupModalConfigSelector,
} from "./selectors";
import {
  selectedPostcodesSelector,
  getSelectedDepot,
  selectedCollectionTabSelector,
  selectedTabSelector,
  selectedOverlaySimulationPercentageSelector,
} from "../ownApp/selectors";
import {
  lookupTableSelector,
  depotDefaultCollectionValuesSelector,
  depotChecksumSelector,
} from "../depot/selectors";
import { getUserMetadata } from "../ownUser/selectors";
import {
  CHOICE_DRIVER_TYPE,
  ROBOT_DRIVER_TYPE,
} from "../../consts/driverTypes";

export const resetMatrix = createAsyncThunk(
  "matrixProfile/resetMatrix",
  // reset matrix values
  () => ({})
);

export const setLoading = createAsyncThunk(
  "matrixProfile/setLoading",
  (value) => value
);

export const refreshTemplateLocks = createAsyncThunk(
  "matrixProfile/refreshTemplateLocks",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const {
      user: { user_id: userCode },
    } = getUserMetadata(state);

    const dayOfWeek = getSelectedPlanDay(state);
    const depotCode = getSelectedDepot(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const driverDayLocks = driverDayLocksSelector(state);
    const routeDayLocks = routeDayLocksSelector(state);

    try {
      const {
        data: {
          driverDayLocks: newDriverDayLocks,
          routeDayLocks: newRouteDayLocks,
        },
      } = await api.get(
        GET_MATRIX_DAY_LOCKS(depotCode, dayOfWeek, sessionChecksum)
      );

      const {
        refreshedDriverDayLocks,
        refreshedRouteDayLocks,
        hasChangedChecksum,
      } = processTemplateLocksRefresh({
        userCode,
        depotCode,
        dayOfWeek,
        driverDayLocks,
        routeDayLocks,
        newDriverDayLocks,
        newRouteDayLocks,
      });

      if (hasChangedChecksum) dispatch(setShowReloadPlanManagement(true));

      return {
        driverDayLocks: refreshedDriverDayLocks,
        routeDayLocks: refreshedRouteDayLocks,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: "Fail to refresh template locks",
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const setUserSessionExpired = createAction(
  "matrixProfile/setUserSessionExpired"
);

export const setLocksRefreshRequired = createAction(
  "matrixProfile/setLocksRefreshRequired"
);

// DRIVER
export const editDriver = createAsyncThunk(
  "matrixProfile/editDriver",
  async ({ route }, { dispatch, getState }) => {
    const state = getState();
    const isDailyPlan = isDailyPlanSelector(state);
    const matrixMode = matrixModeSelector(state);
    const matrixProfileLock = matrixProfileLockSelector(state);
    const routes = routesSelector(state);
    const drivers = driversSelector(state);
    const driver = driverSelector(route.driverCode)(state);
    const routeDayLock = routeDayLockSelector(route.routeCode)(state);
    const driverProfileLock = driverProfileLockSelector(route.driverCode)(
      state
    );
    const driverDayLock = driverDayLockSelector(route.driverCode)(state);
    const odfProfileLock = driverProfileLockSelector(driver?.plannedOdf)(state);
    const odfDayLock = driverDayLockSelector(driver?.plannedOdf)(state);

    if (!isDailyPlan && !matrixProfileLock.lockedByYou) {
      const { error } = await dispatch(lockPlan());
      if (error) throw error;
    } else if (isDailyPlan && matrixProfileLock.lockedByYou) {
      dispatch(
        addNotification({
          message: SAVE_PLAN_BEFORE_EDITING_DRIVER,
          options: { variant: "info" },
        })
      );
      throw new Error(SAVE_PLAN_BEFORE_EDITING_DRIVER);
    } else if (isDailyPlan && routeDayLock.changed) {
      const error = new Error(
        `Route ${route.routeCode} has been changed. Please reload the profile to edit the driver schedule.`
      );
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    } else if (
      isDailyPlan &&
      (driverProfileLock?.changed || driverDayLock?.changed)
    ) {
      const error = new Error(
        `Driver ${driver.driverCode} has been changed. Please reload the profile to edit the driver schedule.`
      );
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    } else if (
      isDailyPlan &&
      (odfProfileLock?.changed || odfDayLock?.changed)
    ) {
      const error = new Error(
        `Driver ${driver.plannedOdf} has been changed. Please reload the profile to edit the driver schedule.`
      );
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }

    dispatch(setRouteWithTimeband(route));
    return {
      matrixMode:
        !isDailyPlan &&
        [MATRIX_MODES.NONE_SELECTED, MATRIX_MODES.VIEW].includes(matrixMode)
          ? MATRIX_MODES.EDIT
          : matrixMode,
      driverEdit: {
        ...driver,
        routeCode: route.routeCode,
      },
      drivers,
      routes,
    };
  }
);

export const lockDriverDay = createAsyncThunk(
  "matrixProfile/lockDriverDay",
  async (driverCode, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const templateId = templateIdSelector(state);
    const dayOfWeek = getSelectedPlanDay(state);
    const driver = driverSelector(driverCode)(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const driverProfileLock = driverProfileLockSelector(driverCode)(state);
    const driverDayLock = driverDayLockSelector(driverCode)(state);

    try {
      const { data: lockResponse } = await api.put(
        LOCK_DRIVER_DAY(driverCode, dayOfWeek),
        null,
        {
          templateId,
          sessionChecksum,
          driverProfileChecksum: driverProfileLock.checksum,
          driverDayChecksum: driverDayLock.checksum,
        }
      );
      if (!lockResponse.success)
        throw new Error(DRIVER_DAY_FAILED_TO_LOCK(driver, dayOfWeek));
      dispatch(
        addNotification({
          message: DRIVER_DAY_SUCESSFULLY_LOCKED(driver, dayOfWeek),
          options: { variant: "success" },
        })
      );
      saveDriverDayChecksum({
        dayOfWeek,
        driverCode,
        checksum: driverDayLock.checksum,
      });
      return {
        driverCode,
        lockedByUserCode: lockResponse.lockedByUserCode,
        lockedByUserName: lockResponse.lockedByUserName,
        lockedDatetime: lockResponse.lockedDatetime,
      };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (
        [DRIVER_PROFILE_CHANGED, DRIVER_PROFILE_LOCKED_FOR_PLAN_COPY].includes(
          error.statusCode
        )
      )
        dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_DAY_CHANGED === error.statusCode)
        dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_PROFILE_LOCKED === error.statusCode)
        (driverProfileLock.lockedByYou ||
          driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_DAY_LOCKED === error.statusCode)
        (driverDayLock.lockedByYou ||
          driverDayLock.lockedInDifferentBrowser ||
          !driverDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_PROFILE_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverProfileLock.lockedByYou ||
          !driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_DAY_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverDayLock.lockedByYou ||
          !driverDayLock.lockedInDifferentBrowser ||
          !driverDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));

      if (
        ![
          MATRIX_TEMPLATE_USER_SESSION_CHANGED,
          DRIVER_PROFILE_CHANGED,
          DRIVER_DAY_CHANGED,
        ].includes(error.statusCode)
      )
        dispatch(
          addNotification({
            message: error.message,
            options: { variant: "error" },
          })
        );
      return rejectWithValue(error.message, error);
    }
  }
);

export const unlockDriverDay = createAsyncThunk(
  "matrixProfile/unlockDriverDay",
  async (driverCode, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const templateId = templateIdSelector(state);
    const dayOfWeek = getSelectedPlanDay(state);
    const driver = driverSelector(driverCode)(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const driverProfileLock = driverProfileLockSelector(driverCode)(state);
    const driverDayLock = driverDayLockSelector(driverCode)(state);

    try {
      const { data: unlockResponse } = await api.put(
        UNLOCK_DRIVER_DAY(driverCode, dayOfWeek),
        null,
        {
          templateId,
          sessionChecksum,
          driverProfileChecksum: driverProfileLock.checksum,
          driverDayChecksum: driverDayLock.checksum,
        }
      );
      if (!unlockResponse.success)
        throw new Error(DRIVER_DAY_FAILED_TO_UNLOCK(driver, dayOfWeek));
      dispatch(
        addNotification({
          message: DRIVER_DAY_SUCESSFULLY_UNLOCKED(driver, dayOfWeek),
          options: { variant: "success" },
        })
      );
      deleteDriverDayChecksum({ dayOfWeek, driverCode });
      return { driverCode };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (
        [DRIVER_PROFILE_CHANGED, DRIVER_PROFILE_LOCKED_FOR_PLAN_COPY].includes(
          error.statusCode
        )
      )
        dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_DAY_CHANGED === error.statusCode)
        dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_PROFILE_LOCKED === error.statusCode)
        (driverProfileLock.lockedByYou ||
          driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_DAY_LOCKED === error.statusCode)
        (driverDayLock.lockedByYou ||
          driverDayLock.lockedInDifferentBrowser ||
          !driverDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_PROFILE_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverProfileLock.lockedByYou ||
          !driverProfileLock.lockedInDifferentBrowser ||
          !driverProfileLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));
      else if (DRIVER_DAY_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!driverDayLock.lockedByYou ||
          !driverDayLock.lockedInDifferentBrowser ||
          !driverDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));

      if (
        ![
          MATRIX_TEMPLATE_USER_SESSION_CHANGED,
          DRIVER_PROFILE_CHANGED,
          DRIVER_DAY_CHANGED,
        ].includes(error.statusCode)
      )
        dispatch(
          addNotification({
            message: error.message,
            options: { variant: "error" },
          })
        );
      return rejectWithValue(error.message, error);
    }
  }
);

export const forceUnlockDrivers = createAsyncThunk(
  "matrixProfile/forceUnlockDrivers",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const type = getMatrixProfileType(state);
    const profile = getSelectedMatrixProfile(state);
    const templateId = templateIdSelector(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const selectedDepot = getSelectedDepot(state);

    try {
      const { data } = await api.put(
        FORCE_UNLOCK_DEPOT_DRIVERS(selectedDepot),
        null,
        {
          templateId,
          sessionChecksum,
        }
      );

      if (!data.success) throw new Error(DRIVER_FORCE_UNLOCK_FAILED);
      deleteDepotDriverProfileChecksums({ depotCode: selectedDepot });
      [0, 1, 2, 3, 4, 5, 6].forEach((dayOfWeek) =>
        deleteDepotDriverDayChecksums({
          dayOfWeek,
          depotCode: selectedDepot,
        })
      );
      dispatch(
        addNotification({
          message: SUCCESS_UNLOCK_DEPOT_DRIVERS,
          options: { variant: "success" },
        })
      );
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }

    await dispatch(fetchDepotRouteMatrix({ type, profile }));
  }
);

export const forceUnlockDriversDay = createAsyncThunk(
  "matrixProfile/forceUnlockDriversDay",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const type = getMatrixProfileType(state);
    const dayOfWeek = getSelectedPlanDay(state);
    const profile = getSelectedMatrixProfile(state);
    const templateId = templateIdSelector(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const selectedDepot = getSelectedDepot(state);

    try {
      const { data } = await api.put(
        FORCE_UNLOCK_DEPOT_DRIVERS_DAY(selectedDepot, dayOfWeek),
        null,
        {
          templateId,
          sessionChecksum,
        }
      );

      if (!data.success) throw new Error(DRIVER_FORCE_UNLOCK_FAILED);
      deleteDepotDriverDayChecksums({
        dayOfWeek,
        depotCode: selectedDepot,
      });
      dispatch(
        addNotification({
          message: SUCCESS_UNLOCK_DEPOT_DRIVERS,
          options: { variant: "success" },
        })
      );
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }

    await dispatch(fetchDepotRouteMatrix({ type, profile }));
  }
);

export const updateDriverEdit = createAsyncThunk(
  "matrixProfile/updateDriverEdit",
  async ({ name, value }, { dispatch, getState }) => {
    const state = getState();
    const isDailyPlan = isDailyPlanSelector(state);
    const drivers = driversSelector(state);
    const routes = routesSelector(state);
    const driverEdit = driverInEditSelector(state);
    const driverEditCopy = driverInEditCopySelector(state);

    const newDriverEdit = await updateDriverDay({
      disableLocking: !isDailyPlan,
      update: { name, value },
      drivers,
      routes,
      driverEdit,
      driverEditCopy,

      getRouteDayLock: (routeCode) =>
        routeDayLockSelector(routeCode)(getState()),
      lockRouteDayAction: (routeCode) => dispatch(lockRouteDay(routeCode)),
      unlockRouteDayAction: (routeCode) => dispatch(unlockRouteDay(routeCode)),

      getDriverDayLock: (driverCode) =>
        driverDayLockSelector(driverCode)(getState()),
      lockDriverDayAction: (driverCode) => dispatch(lockDriverDay(driverCode)),
      unlockDriverDayAction: (driverCode) =>
        dispatch(unlockDriverDay(driverCode)),

      addNotificationAction: (message) =>
        dispatch(
          addNotification({
            message,
            options: { variant: "error" },
          })
        ),

      onFinally: async () => {
        const afterLockingState = getState();
        const userSessionExpired =
          userSessionExpiredSelector(afterLockingState);
        const locksRefreshRequired =
          locksRefreshRequiredSelector(afterLockingState);

        if (userSessionExpired || locksRefreshRequired)
          dispatch(closeDriverEdit());
        else dispatch(setLoading(false));
      },
    });
    return newDriverEdit;
  }
);

export const processDriverEdit = createAsyncThunk(
  "matrixProfile/processDriverEdit",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const isTemplate = isTemplateSelector(state);
    const planDay = getSelectedPlanDay(state);
    const planDate = getSelectedPlanDate(state);
    const isDailyPlan = isDailyPlanSelector(state);
    const driverEdit = driverInEditSelector(state);
    const driverEditCopy = driverInEditCopySelector(state);
    const drivers = driversSelector(state);
    const routes = routesSelector(state);
    const vehicles = vehiclesSelector(state);
    const depotRoutesOptimised = depotRoutesOptimisedSelector(state);
    const volumeData = volumeDataSelector(state);

    const {
      impactedRoutes,
      impactedDrivers,
      routes: newRoutes,
      drivers: newDrivers,
    } = applyChangeToMatrixDay({
      dayOfWeek: !isTemplate && (planDay || moment(planDate).day()),
      driverEdit,
      driverEditCopy,
      drivers,
      routes,
    });

    if (!depotRoutesOptimised || !volumeData) {
      let keepLoading = false;
      if (isDailyPlan) {
        const { error, meta } = await dispatch(
          saveDriverDays({ impactedDrivers, impactedRoutes })
        );
        keepLoading =
          error &&
          [DRIVER_DAY_CHANGED, ROUTE_DAY_CHANGED].includes(meta.statusCode);
        if (error && !keepLoading) throw error;
      }

      return {
        routes: newRoutes,
        drivers: newDrivers,
        driversImpacted: null,
        routesImpacted: null,
        showOptimiseResults: false,
        loading: keepLoading,
      };
    } else {
      const { payload, error } = await dispatch(
        optimiseImpactedRoutes({
          impactedRoutes,
          routes: newRoutes,
          drivers: newDrivers,
          vehicles,
        })
      );
      if (error) throw error;
      return {
        routes: payload.routes,
        drivers: newDrivers,
        driversImpacted: impactedDrivers,
        routesImpacted: impactedRoutes.reduce((acc, { routeCode }) => {
          acc[routeCode] = { optimised: true };
          return acc;
        }, {}),
        showOptimiseResults: true,
      };
    }
  }
);

export const saveDriverDays = createAsyncThunk(
  "matrixProfile/saveDriverDays",
  async (
    { impactedDrivers, impactedRoutes },
    { getState, dispatch, rejectWithValue }
  ) => {
    const state = getState();
    const templateId = templateIdSelector(state);
    const selectedDepot = getSelectedDepot(state);
    const dayOfWeek = getSelectedPlanDay(state);
    const routeDayLocks = routeDayLocksSelector(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const driverProfileLocks = driverProfileLocksSelector(state);
    const driverDayLocks = driverDayLocksSelector(state);

    try {
      const { data: saveResponse } = await api.put(
        UPDATE_MATRIX_DAY(selectedDepot, dayOfWeek, templateId),
        null,
        {
          sessionChecksum,
          routeDays: generateRouteDaysConfig({
            routes: impactedRoutes,
            routeDayLocks,
          }),
          drivers: generateDriverProfilesConfig({
            drivers: impactedDrivers,
            driverProfileLocks,
          }),
          driverDays: generateDriverDaysConfig({
            drivers: impactedDrivers,
            driverDayLocks,
          }),
        }
      );
      if (!saveResponse.success) throw new Error("Failed to save drivers");

      const newRouteDayLocks = Object.keys(saveResponse.routeDayLocks).reduce(
        (acc, routeCode) => {
          const {
            checksum,
            lockedByUserCode,
            lockedByUserName,
            lockedDatetime,
          } = saveResponse.routeDayLocks[routeCode];
          acc[routeCode] = {
            checksum,
            lockedByUserCode,
            lockedByUserName,
            lockedDatetime,
            lockedByYou: true,
            lockedInDifferentBrowser: false,
          };
          saveRouteDayChecksum({
            dayOfWeek,
            depotCode: selectedDepot,
            routeCode,
            checksum,
          });
          return acc;
        },
        { ...routeDayLocks }
      );

      const newDriverDayLocks = Object.keys(saveResponse.driverDayLocks).reduce(
        (acc, driverCode) => {
          const {
            checksum,
            lockedByUserCode,
            lockedByUserName,
            lockedDatetime,
          } = saveResponse.driverDayLocks[driverCode];
          acc[driverCode] = {
            checksum,
            lockedByUserCode,
            lockedByUserName,
            lockedDatetime,
            lockedByYou: true,
            lockedInDifferentBrowser: false,
          };
          saveDriverDayChecksum({ dayOfWeek, driverCode, checksum });
          return acc;
        },
        { ...driverDayLocks }
      );

      dispatch(
        addNotification({
          message: `Successfully saved driver changes.`,
          options: { variant: "success" },
        })
      );

      syncToAsync(() => dispatch(closeDriverEdit()));
      return {
        routeDayLocks: newRouteDayLocks,
        driverDayLocks: newDriverDayLocks,
      };
    } catch (error) {
      if (
        ![
          DRIVER_PROFILE_CHANGED,
          DRIVER_DAY_CHANGED,
          ROUTE_DAY_CHANGED,
        ].includes(error.statusCode)
      )
        dispatch(
          addNotification({
            message: error.message,
            options: { variant: "error" },
          })
        );
      else dispatch(closeDriverEdit());
      return rejectWithValue(error.message, error);
    }
  }
);

export const applyDriverEdit = createAsyncThunk(
  "matrixProfile/applyDriverEdit",
  async (params, { getState, dispatch }) => {
    const state = getState();
    const impactedRoutes = routeCodesImpactedSelector(state);
    const impactedDrivers = driversImpactedSelector(state);
    const { error, meta } = await dispatch(
      saveDriverDays({
        impactedDrivers,
        impactedRoutes,
      })
    );
    if (
      error &&
      ![DRIVER_DAY_CHANGED, ROUTE_DAY_CHANGED].includes(meta.statusCode)
    )
      throw error;
  }
);

export const revertDriverEdit = createAsyncThunk(
  "matrixProfile/revertDriverEdit",
  () => ({})
);

export const closeDriverEdit = createAsyncThunk(
  "matrixProfile/closeDriverEdit",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const type = getMatrixProfileType(state);
    const profile = getSelectedMatrixProfile(state);
    const isDailyPlan = isDailyPlanSelector(state);
    const drivers = driversSelector(state);
    const routeDayLocks = routeDayLocksSelector(state);
    const driverDayLocks = driverDayLocksSelector(state);

    if (isDailyPlan) {
      const changedRoutes = new Set();
      const changedDrivers = new Set();
      await Promise.allSettled([
        ...Object.entries(routeDayLocks)
          .filter(
            ([routeCode, { lockedByYou, lockedInDifferentBrowser }]) =>
              lockedByYou && !lockedInDifferentBrowser
          )
          .map(async ([routeCode]) => {
            const { error, meta } = await dispatch(unlockRouteDay(routeCode));
            if (error && meta.statusCode === ROUTE_DAY_CHANGED)
              changedRoutes.add(routeCode);
            if (error) throw error;
          }),
        ...Object.entries(driverDayLocks)
          .filter(
            ([driverCode, { lockedByYou, lockedInDifferentBrowser }]) =>
              lockedByYou && !lockedInDifferentBrowser
          )
          .map(async ([driverCode]) => {
            const { error, meta } = await dispatch(unlockDriverDay(driverCode));
            if (
              error &&
              [DRIVER_PROFILE_CHANGED, DRIVER_DAY_CHANGED].includes(
                meta.statusCode
              )
            )
              changedDrivers.add(drivers[driverCode].driverName);
            if (error) throw error;
          }),
      ]);

      if (changedRoutes.size || changedDrivers.size)
        await dispatch(fetchDepotRouteMatrix({ type, profile }));
      else {
        const afterUnlockingState = getState();
        const userSessionExpired =
          userSessionExpiredSelector(afterUnlockingState);
        const locksRefreshRequired =
          locksRefreshRequiredSelector(afterUnlockingState);

        if (userSessionExpired)
          dispatch(setPlanManagementExpiredModalOpen(true));
        else if (locksRefreshRequired) await dispatch(refreshTemplateLocks());
      }
    }
  }
);

// EDIT ROUTES
export const editRoute = createAsyncThunk(
  "matrixProfile/editRoute",
  async ({ route }, { getState, dispatch }) => {
    const state = getState();
    const planType = getMatrixProfileType(state);
    const isDailyPlan = isDailyPlanSelector(state);
    const routesModified = routesModifiedSelector(state);
    const matrixProfileLock = matrixProfileLockSelector(state);
    const routeDayLock = routeDayLockSelector(route.routeCode)(state);
    const routeConfigEdit = routeConfigEditSelector(state);
    const redirectConfig = redirectConfigSelector(state);
    const drivers = driversSelector(state);
    const routes = routesSelector(state);
    const collectionAreas = collectionAreasSelector(state);
    const pickupAreas = pickupAreasSelector(state);
    const overrideAreas = overrideAreasSelector(state);
    const locationConfig = locationConfigSelector(state);
    const routesTimes = routesTimesSelector(state);
    const routesByOuter = routesByOuterSelector(state);

    const routeDriver = drivers[route.driverCode];
    const routeDriverType = routeDriver?.driverType;
    if (
      !!routeDriver &&
      // NON-ROBOT DRIVER ON ROBOT PLAN
      ((ROBOTS_PLAN_TYPE === planType &&
        ROBOT_DRIVER_TYPE !== routeDriverType) ||
        // ROBOT DRIVER ON NON-ROBOT PLAN
        (ROBOTS_PLAN_TYPE !== planType &&
          ROBOT_DRIVER_TYPE === routeDriverType) ||
        // NON-CHOICE DRIVER ON CHOICE PLAN
        (CHOICE_PLAN_TYPE === planType &&
          CHOICE_DRIVER_TYPE !== routeDriverType) ||
        // CHOICE DRIVER ON NON-CHOICE PLAN
        (CHOICE_PLAN_TYPE !== planType &&
          CHOICE_DRIVER_TYPE === routeDriverType))
    ) {
      dispatch(
        addNotification({
          message: `Route ${route.routeCode} has a driver ${
            routeDriver.driverCode.split("*")[1]
          } ${
            routeDriver.driverName
          } who is not suitable for a selected plan type.`,
          options: { variant: "error" },
        })
      );
      throw new Error("Driver is not suitable for plan type.");
    }

    if (!matrixProfileLock.lockedByYou) {
      const { error } = await dispatch(lockPlan());
      if (error) throw error;
    }

    if (
      isDailyPlan &&
      (!routeDayLock.lockedByYou || routeDayLock.lockedInDifferentBrowser)
    ) {
      const { error, meta } = await dispatch(lockRouteDay(route.routeCode));
      // This will unlock plan if failed to lock route
      // but only if plan wasn't locked already when editing other route.
      if (error && !matrixProfileLock.lockedByYou) {
        const { error: unlockPlanError } = await dispatch(
          unlockPlan({ keepLoading: true })
        );
        if (unlockPlanError) throw unlockPlanError;
      }
      const afterLockingState = getState();
      const userSessionExpired = userSessionExpiredSelector(afterLockingState);
      const locksRefreshRequired =
        locksRefreshRequiredSelector(afterLockingState);
      if (userSessionExpired) dispatch(setPlanManagementExpiredModalOpen(true));
      else if (error && locksRefreshRequired) {
        await dispatch(refreshTemplateLocks());
        if (meta.statusCode === ROUTE_DAY_CHANGED) {
          dispatch(setShowReloadPlanManagement(true));
          dispatch(
            addNotification({
              message: `Route ${route.routeCode} has been changed. Please reload the profile to edit the route.`,
              options: { variant: "error" },
            })
          );
        }
      }
      if (error) throw error;
    }

    if (isDailyPlan && routeDriver) {
      const afterLockingState = getState();
      const routeOdf = drivers[routeDriver?.plannedOdf];

      const routeDriverProfileLock = driverProfileLockSelector(
        routeDriver.driverCode
      )(afterLockingState);
      const routeDriverDayLock = driverDayLockSelector(routeDriver.driverCode)(
        afterLockingState
      );
      const routeOdfProfileLock = driverProfileLockSelector(
        routeOdf?.driverCode
      )(afterLockingState);
      const routeOdfDayLock = driverDayLockSelector(routeOdf?.driverCode)(
        afterLockingState
      );

      (routeDriverProfileLock.lockedByUserCode ||
        routeDriverDayLock.lockedByUserCode ||
        routeOdfProfileLock?.lockedByUserCode ||
        routeOdfDayLock?.lockedByUserCode) &&
        (await dispatch(refreshTemplateLocks()));
    }

    dispatch(setRouteWithTimeband(route));
    return {
      routeEdit: route,
      routeConfig: routeConfigEdit,
      redirectConfig,
      routes,
      routesTimes,
      routesByOuter,
      collectionAreas,
      overrideAreas,
      pickupAreas,
      locationConfig,
      routesModified,
    };
  }
);

export const setRouteDayLocks = createAsyncThunk(
  "matrixProfile/setRouteDayLocks",
  (routeDayLocks) => routeDayLocks
);

export const lockRouteDay = createAsyncThunk(
  "matrixProfile/lockRouteDay",
  async (routeCode, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const templateId = templateIdSelector(state);
    const depotCode = getSelectedDepot(state);
    const dayOfWeek = getSelectedPlanDay(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const routeDayLock = routeDayLockSelector(routeCode)(state);

    try {
      if (routeDayLock.changed)
        throw new Error(
          `Route ${routeCode} has been changed. Please reload the profile to edit the route.`
        );
      const { data: lockResponse } = await api.put(
        LOCK_ROUTE_DAY(depotCode, dayOfWeek, routeCode),
        null,
        {
          templateId,
          sessionChecksum,
          checksum: routeDayLock.checksum,
        }
      );
      if (!lockResponse.success)
        throw new Error(ROUTE_DAY_FAILED_TO_LOCK(routeCode, dayOfWeek));
      dispatch(
        addNotification({
          message: ROUTE_DAY_SUCESSFULLY_LOCKED(routeCode, dayOfWeek),
          options: { variant: "success" },
        })
      );
      saveRouteDayChecksum({
        dayOfWeek,
        depotCode,
        routeCode,
        checksum: routeDayLock.checksum,
      });
      return {
        routeCode,
        lockedByUserCode: lockResponse.lockedByUserCode,
        lockedByUserName: lockResponse.lockedByUserName,
        lockedDatetime: lockResponse.lockedDatetime,
      };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (ROUTE_DAY_CHANGED === error.statusCode)
        dispatch(setLocksRefreshRequired(true));
      else if (ROUTE_DAY_LOCKED === error.statusCode)
        (routeDayLock.lockedByYou ||
          routeDayLock.lockedInDifferentBrowser ||
          !routeDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));
      else if (ROUTE_DAY_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!routeDayLock.lockedByYou ||
          !routeDayLock.lockedInDifferentBrowser ||
          !routeDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));

      if (
        ![MATRIX_TEMPLATE_USER_SESSION_CHANGED, ROUTE_DAY_CHANGED].includes(
          error.statusCode
        )
      )
        dispatch(
          addNotification({
            message: error.message,
            options: { variant: "error" },
          })
        );
      return rejectWithValue(error.message, error);
    }
  }
);

export const unlockRouteDay = createAsyncThunk(
  "matrixProfile/unlockRouteDay",
  async (routeCode, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const templateId = templateIdSelector(state);
    const depotCode = getSelectedDepot(state);
    const dayOfWeek = getSelectedPlanDay(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const routeDayLock = routeDayLockSelector(routeCode)(state);

    if (routeDayLock.changed)
      throw new Error(
        `Route ${routeCode} has been changed. Please reload the profile to edit the route.`
      );

    try {
      const { data: unlockResponse } = await api.put(
        UNLOCK_ROUTE_DAY(depotCode, dayOfWeek, routeCode),
        null,
        {
          templateId,
          sessionChecksum,
          checksum: routeDayLock.checksum,
        }
      );
      if (!unlockResponse.success)
        throw new Error(ROUTE_DAY_FAILED_TO_UNLOCK(routeCode, dayOfWeek));
      dispatch(
        addNotification({
          message: ROUTE_DAY_SUCESSFULLY_UNLOCKED(routeCode, dayOfWeek),
          options: { variant: "success" },
        })
      );
      deleteRouteDayChecksum({ dayOfWeek, depotCode, routeCode });
      return { routeCode };
    } catch (error) {
      if (MATRIX_TEMPLATE_USER_SESSION_CHANGED === error.statusCode)
        dispatch(setUserSessionExpired(true));
      else if (ROUTE_DAY_CHANGED === error.statusCode)
        dispatch(setLocksRefreshRequired(true));
      else if (ROUTE_DAY_LOCKED === error.statusCode)
        (routeDayLock.lockedByYou ||
          routeDayLock.lockedInDifferentBrowser ||
          !routeDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));
      else if (ROUTE_DAY_LOCKED_IN_OTHER_BROWSER === error.statusCode)
        (!routeDayLock.lockedByYou ||
          !routeDayLock.lockedInDifferentBrowser ||
          !routeDayLock.lockedByUserCode) &&
          dispatch(setLocksRefreshRequired(true));

      if (
        ![MATRIX_TEMPLATE_USER_SESSION_CHANGED, ROUTE_DAY_CHANGED].includes(
          error.statusCode
        )
      )
        dispatch(
          addNotification({
            message: error.message,
            options: { variant: "error" },
          })
        );
      return rejectWithValue(error.message, error);
    }
  }
);

export const cancelAllEdits = createAsyncThunk(
  "matrixProfile/cancelAllEdits",
  async (params, { getState, dispatch }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const { matrixProfile } = state.matrixProfile.routeMatrix;
    const { type, profile } = state.matrixProfile;
    const routeDayLocks = routeDayLocksSelector(state);
    const isDailyPlan = isDailyPlanSelector(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const checksum = matrixProfileChecksumSelector(state);
    const depotChecksum = depotChecksumSelector(state);

    try {
      const changedRoutes = new Set();
      if (isDailyPlan)
        await Promise.allSettled(
          Object.entries(routeDayLocks)
            .filter(
              ([routeCode, { lockedByYou, lockedInDifferentBrowser }]) =>
                lockedByYou && !lockedInDifferentBrowser
            )
            .map(async ([routeCode]) => {
              const { error, meta } = await dispatch(unlockRouteDay(routeCode));
              if (error && meta.statusCode === ROUTE_DAY_CHANGED)
                changedRoutes.add(routeCode);
              if (error) throw error;
            })
        );

      dispatch(resetSelectedPostcodes());
      dispatch(setCollectionTimeEdit(false));
      const { data: unlockResponse } = await api.post(
        POST_UNLOCK_TEMPLATE(selectedDepot, matrixProfile.templateId),
        null,
        {
          sessionChecksum,
          checksum,
          depotChecksum,
          forceUnlock: false,
        }
      );
      if (!unlockResponse.success) throw new Error(PLAN_FAILED_TO_UNLOCK);
      deleteTemplateChecksum(matrixProfile.templateId);

      if (changedRoutes.size)
        dispatch(
          addNotification({
            message: ROUTE_DAY_HAS_CHANGED({
              codes: Array.from(changedRoutes),
            }),
            options: { variant: "error" },
          })
        );
      dispatch(
        addNotification({
          message: SUCCESS_UNLOCK_PLAN,
          options: { variant: "success" },
        })
      );

      await dispatch(fetchDepotRouteMatrix({ type, profile }));
      dispatch(setSelectedRoute(null));
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      if (error.statusCode === DEPOT_MAP_CHANGED)
        await dispatch(fetchLookUpTables(selectedDepot));
      if (
        [DEPOT_MAP_CHANGED, MATRIX_TEMPLATE_CHANGED].includes(error.statusCode)
      )
        await dispatch(fetchDepotRouteMatrix({ type, profile }));
      else if (error.statusCode === MATRIX_TEMPLATE_USER_SESSION_CHANGED)
        dispatch(setPlanManagementExpiredModalOpen(true));
      throw error;
    }
  }
);

export const lockPlan = createAsyncThunk(
  "matrixProfile/lockPlan",
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    let type, profile, selectedDepot;
    try {
      type = getMatrixProfileType(state);
      profile = getSelectedMatrixProfile(state);
      selectedDepot = getSelectedDepot(state);
      const { matrixProfile } = getRouteMatrix(state);
      const sessionChecksum = matrixProfileSessionChecksumSelector(state);
      const checksum = matrixProfileChecksumSelector(state);
      const depotChecksum = depotChecksumSelector(state);

      const { data: lockResponse } = await api.post(
        POST_LOCK_TEMPLATE(selectedDepot, matrixProfile.templateId),
        null,
        {
          sessionChecksum,
          checksum,
          depotChecksum,
        }
      );
      if (!lockResponse.success) throw new Error(PLAN_FAILED_TO_LOCK);
      saveTemplateChecksum(matrixProfile.templateId, checksum);
      dispatch(
        addNotification({
          message: SUCCESS_LOCK_PLAN,
          options: { variant: "success" },
        })
      );
      return lockResponse;
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      if (error.statusCode === DEPOT_MAP_CHANGED)
        await dispatch(fetchLookUpTables(selectedDepot));
      if (
        [DEPOT_MAP_CHANGED, MATRIX_TEMPLATE_CHANGED].includes(error.statusCode)
      )
        await dispatch(fetchDepotRouteMatrix({ type, profile }));
      else if (error.statusCode === MATRIX_TEMPLATE_USER_SESSION_CHANGED)
        dispatch(setPlanManagementExpiredModalOpen(true));
      return rejectWithValue(error.message);
    }
  }
);

export const unlockPlan = createAsyncThunk(
  "matrixProfile/unlockPlan",
  async (
    { forceUnlock = false, keepLoading = false },
    { getState, dispatch }
  ) => {
    const state = getState();
    const isDailyPlan = isDailyPlanSelector(state);
    const dayOfWeek = getSelectedPlanDay(state);
    const type = getMatrixProfileType(state);
    const profile = getSelectedMatrixProfile(state);
    const selectedDepot = getSelectedDepot(state);
    const { matrixProfile } = getRouteMatrix(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const checksum = matrixProfileChecksumSelector(state);
    const depotChecksum = depotChecksumSelector(state);

    try {
      const { data: unlockResponse } = await api.post(
        POST_UNLOCK_TEMPLATE(selectedDepot, matrixProfile.templateId),
        null,
        {
          sessionChecksum,
          checksum,
          depotChecksum,
          forceUnlock,
        }
      );
      if (!unlockResponse.success) throw new Error(PLAN_FAILED_TO_UNLOCK);
      deleteTemplateChecksum(matrixProfile.templateId);
      if (forceUnlock && isDailyPlan) {
        deleteDepotRouteDayChecksums({
          dayOfWeek,
          depotCode: selectedDepot,
        });
        deleteDepotDriverDayChecksums({
          dayOfWeek,
          depotCode: selectedDepot,
        });
        dispatch(
          addNotification({
            message: SUCCESS_UNLOCK_MATRIX_DAY({ dayOfWeek }),
            options: { variant: "success" },
          })
        );
      } else
        dispatch(
          addNotification({
            message: SUCCESS_UNLOCK_PLAN,
            options: { variant: "success" },
          })
        );
      return { keepLoading };
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      if (error.statusCode === DEPOT_MAP_CHANGED)
        await dispatch(fetchLookUpTables(selectedDepot));
      if (
        [DEPOT_MAP_CHANGED, MATRIX_TEMPLATE_CHANGED].includes(error.statusCode)
      )
        await dispatch(fetchDepotRouteMatrix({ type, profile }));
      else if (error.statusCode === MATRIX_TEMPLATE_USER_SESSION_CHANGED)
        dispatch(setPlanManagementExpiredModalOpen(true));
      throw error;
    }
  }
);

export const processRouteEditAction = createAsyncThunk(
  "matrixProfile/processRouteEditAction",
  async ({ change }, { getState, dispatch }) => {
    const state = getState();
    const routes = routesSelector(state);
    const editRoutesCopy = editRoutesCopySelector(state);
    const routeConfigEdit = routeConfigEditSelector(state);
    const redirectConfig = redirectConfigSelector(state);
    const lookupTables = lookupTableSelector(state);
    const routesImpacted = routesImpactedSelector(state);
    const routesTimesCopy = routesTimesCopySelector(state);
    const routesByOuter = routesByOuterSelector(state);
    const routesTimes = routesTimesSelector(state);
    const cutOffTimes = cutOffTimesSelector(state);
    const overrideAreas = overrideAreasSelector(state);
    const pickupAreas = pickupAreasSelector(state);
    const locationConfig = locationConfigSelector(state);
    const volumeData = volumeDataSelector(state);

    try {
      const {
        routes: newRoutes,
        routesImpacted: newRoutesImpacted,
        routeConfig: newRouteConfig,
        redirectConfig: newRedirectConfig,
        overlayDetails,
        routesByOuter: newRoutesByOuter,
        routesTimes: newRoutesTimes,
        cutOffTimes: newCutOffTimes,
      } = await applyChangeToProfile({
        routes,
        routeConfig: routeConfigEdit,
        redirectConfig,
        routesImpacted,
        preEditRoutes: editRoutesCopy,
        routesTimes,
        preEditRoutesTimes: routesTimesCopy,
        routesByOuter,
        cutOffTimes,
        volumeData,
        overrideAreas,
        pickupAreas,
        locationConfig,
        lookupTables,
        change,
      });

      return {
        routes: newRoutes,
        routeEdit: newRoutes.find(
          ({ routeCode }) => routeCode === change.routeEditCode
        ),
        action: change,
        routesImpacted: newRoutesImpacted,
        routeConfigEdit: newRouteConfig,
        redirectConfig: newRedirectConfig,
        overlayDetails,
        routesByOuter: newRoutesByOuter,
        routesTimes: newRoutesTimes,
        cutOffTimes: newCutOffTimes,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_PROCESSING_EDIT,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const clearRoute = createAsyncThunk(
  "matrixProfile/clearRoute",
  ({ routeCode }, { getState, dispatch }) => {
    const planType = getMatrixProfileType(getState());
    if (planType === COLLECTIONS_PLAN_TYPE)
      dispatch(clearRouteCollectionAreas({ routeCode }));
    else if (planType === LOCATIONS_PLAN_TYPE)
      dispatch(clearRouteLocations({ routeCode }));
    else if (planType === OVERRIDES_PLAN_TYPE)
      dispatch(clearRouteOverrideAreas({ routeCode }));
    else if (planType === PICKUP_PLAN_TYPE)
      dispatch(clearRoutePickupAreas({ routeCode }));
    else dispatch(clearRouteDeliveryAreas({ routeCode }));
  }
);

export const clearRouteDeliveryAreas = createAsyncThunk(
  "matrixProfile/clearRouteDeliveryAreas",
  async ({ routeCode }, { getState, dispatch }) => {
    try {
      const state = getState();
      const route = routeSelector(routeCode)(state);
      const planType = getMatrixProfileType(state);
      const routeEditCode = routeInEditCodeSelector(state);
      const routeConfig = routeConfigEditSelector(state);
      const redirectConfig = redirectConfigSelector(state);
      const routes = routesSelector(state);
      const overrideAreas = overrideAreasSelector(state);
      const pickupAreas = pickupAreasSelector(state);
      const locationConfig = locationConfigSelector(state);
      const routesTimes = routesTimesSelector(state);
      const routesByOuter = routesByOuterSelector(state);
      const lookupTables = lookupTableSelector(state);
      const volumeData = volumeDataSelector(state);
      const cutOffTimes = cutOffTimesSelector(state);
      const routesImpacted = routesImpactedSelector(state);
      const editRoutesCopy = editRoutesCopySelector(state);
      const routesTimesCopy = routesTimesCopySelector(state);

      let newRoutes = routes;
      let newRoutesImpacted = routesImpacted;
      let newRouteConfig = routeConfig;
      let newRedirectConfig = redirectConfig;
      let newRoutesByOuter = routesByOuter;
      let newRoutesTimes = routesTimes;
      let newCutOffTimes = cutOffTimes;
      let newOverlayDetails = volumeData?.overlayDetails;

      const areas = [
        ...route.outer.map(({ postcodeOuter }) => postcodeOuter),
        ...route.sector.map(({ postcodeSector }) => postcodeSector),
        ...route.postcode.map(({ postcode }) => postcode),
      ];

      await serialAsyncForEach(areas, async (postcodeKey) => {
        const options = getRouteEditOptions({
          postcodeKey,
          routeConfig: newRouteConfig,
          redirectConfig: newRedirectConfig,
          planType,
          selectedTimeBand: route.plannedReadyTimeAsString || DEFAULT_TIMEBAND,
          routeEditCode: routeCode,
          routesByOuter: newRoutesByOuter,
          lookupTables,
        });

        ({
          routes: newRoutes,
          routesImpacted: newRoutesImpacted,
          routeConfig: newRouteConfig,
          redirectConfig: newRedirectConfig,
          overlayDetails: newOverlayDetails,
          routesByOuter: newRoutesByOuter,
          routesTimes: newRoutesTimes,
          cutOffTimes: newCutOffTimes,
        } = await applyChangeToProfile({
          routes: newRoutes,
          routeConfig: newRouteConfig,
          redirectConfig: newRedirectConfig,
          routesImpacted: newRoutesImpacted,
          preEditRoutes: editRoutesCopy,
          routesTimes: newRoutesTimes,
          preEditRoutesTimes: routesTimesCopy,
          routesByOuter: newRoutesByOuter,
          cutOffTimes: newCutOffTimes,
          volumeData: newOverlayDetails && {
            overlayDetails: newOverlayDetails,
          },
          overrideAreas,
          pickupAreas,
          locationConfig,
          lookupTables,
          change: options.find(({ type }) => type === REMOVE),
        }));
      });

      return {
        routes: newRoutes,
        routeEdit: newRoutes.find((route) => route.routeCode === routeEditCode),
        routesImpacted: newRoutesImpacted,
        routeConfigEdit: newRouteConfig,
        redirectConfig: newRedirectConfig,
        overlayDetails: newOverlayDetails,
        routesByOuter: newRoutesByOuter,
        routesTimes: newRoutesTimes,
        cutOffTimes: newCutOffTimes,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_PROCESSING_EDIT,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const clearRouteCollectionAreas = createAsyncThunk(
  "matrixProfile/clearRouteCollectionAreas",
  async ({ routeCode }, { getState, dispatch }) => {
    try {
      const state = getState();
      const route = routeSelector(routeCode)(state);
      const routeEditCode = routeInEditCodeSelector(state);
      const routes = routesSelector(state);
      const routesByOuter = routesByOuterSelector(state);
      const lookupTables = lookupTableSelector(state);
      const routesImpacted = routesImpactedSelector(state);
      const editRoutesCopy = editRoutesCopySelector(state);
      const collectionAreas = collectionAreasSelector(state);
      const collectionSelectedTab = selectedTabSelector(state);

      let newRoutes = routes;
      let newRoutesImpacted = routesImpacted;
      let newCollectionAreas = collectionAreas;
      let newRoutesByOuter = routesByOuter;

      const filterCollectionType = ({ collectionType }) =>
        collectionType === COLLECTION_TAB_FOR_TYPE[collectionSelectedTab];

      const outers = route.outer
        .filter(filterCollectionType)
        .map(({ postcodeOuter }) => postcodeOuter);
      const sectors = route.sector
        .filter(filterCollectionType)
        .map(({ postcodeSector }) => postcodeSector)
        .filter((postcode) => !outers.includes(Postcode.toOuter(postcode)));
      const postcodes = route.postcode
        .filter(filterCollectionType)
        .map(({ postcode }) => postcode)
        .filter(
          (postcode) =>
            !outers.includes(Postcode.toOuter(postcode)) &&
            !sectors.includes(Postcode.toSector(postcode))
        );

      await asyncLoop(
        [...outers, ...sectors, ...postcodes],
        "forEach",
        (postcodeKey) => {
          const change = getCollectionEditAction({
            postcodeKey,
            routesByOuter: newRoutesByOuter,
            routeEditCode: routeCode,
            collectionAreas,

            collectionSelectedTab,
            lookupTables,
          });

          ({
            routes: newRoutes,
            routesImpacted: newRoutesImpacted,
            collectionAreas: newCollectionAreas,
            routesByOuter: newRoutesByOuter,
          } = applyChangeToCollections({
            routes: newRoutes,
            preEditRoutes: editRoutesCopy,
            routesImpacted: newRoutesImpacted,
            routesByOuter: newRoutesByOuter,
            collectionAreas: newCollectionAreas,
            collectionSelectedTab,
            lookupTables,
            change,
          }));
        }
      );

      return {
        routeEdit: newRoutes.find(
          ({ routeCode }) => routeCode === routeEditCode
        ),
        routes: newRoutes,
        collectionAreas: newCollectionAreas,
        routesImpacted: newRoutesImpacted,
        routesByOuter: newRoutesByOuter,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_PROCESSING_EDIT,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const clearRouteLocations = createAsyncThunk(
  "matrixProfile/clearRouteLocations",
  async ({ routeCode }, { getState, dispatch }) => {
    try {
      const state = getState();
      const routeConfigEdit = routeConfigEditSelector(state);
      const volumeData = volumeDataSelector(state);
      const routes = routesSelector(state);
      const lookupTables = lookupTableSelector(state);
      const locationConfig = locationConfigSelector(state);
      const redirectConfig = redirectConfigSelector(state);
      const overrideAreas = overrideAreasSelector(state);
      const pickupAreas = pickupAreasSelector(state);

      const newRoutes = routes.map((route) =>
        route.routeCode !== routeCode
          ? route
          : { ...route, locationCodes: [], locationRoute: false }
      );
      const newLocationConfig = locationConfig.map((location) =>
        location.routeCode !== routeCode
          ? location
          : { ...location, routeCode: null }
      );

      const { routesWithVolume, processedVolumeData } =
        await processRoutesProfile({
          routeConfig: routeConfigEdit,
          redirectConfig,
          routes: newRoutes,
          volumeData,
          overrideAreas,
          pickupAreas,
          locationConfig: newLocationConfig,
          lookupTables,
          planType: LOCATIONS_PLAN_TYPE,
        });

      return {
        routeEdit: newRoutes.find((route) => route.routeCode === routeCode),
        routes: routesWithVolume,
        volumeData: processedVolumeData,
        locationConfig: newLocationConfig,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_PROCESSING_EDIT,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const clearRouteOverrideAreas = createAsyncThunk(
  "matrixProfile/clearRouteOverrideAreas",
  async ({ routeCode }, { dispatch, getState }) => {
    try {
      const state = getState();
      const routeConfigEdit = routeConfigEditSelector(state);
      const volumeData = volumeDataSelector(state);
      const lookupTables = lookupTableSelector(state);
      const routes = routesSelector(state);
      const redirectConfig = redirectConfigSelector(state);
      const overrideAreas = overrideAreasSelector(state);
      const pickupAreas = pickupAreasSelector(state);
      const locationConfig = locationConfigSelector(state);

      const newOverrideAreas = [OUTER, SECTOR, POSTCODE].reduce(
        (acc, level) => {
          const levelKey = level.toLowerCase();
          acc[levelKey] = getOverridesWithConflicts(
            overrideAreas[levelKey].filter(
              (override) => override.routeCode !== routeCode
            )
          );
          return acc;
        },
        {}
      );

      const {
        routesWithVolume,
        routesByOuter,
        routesTimes,
        cutOffTimes,
        processedVolumeData,
      } = await processRoutesProfile({
        routeConfig: routeConfigEdit,
        redirectConfig,
        routes: routes.map((route) =>
          route.routeCode !== routeCode
            ? route
            : {
                ...route,
                overridesAllocated: false,
              }
        ),
        volumeData,
        overrideAreas: newOverrideAreas,
        pickupAreas,
        locationConfig,
        lookupTables,
        planType: OVERRIDES_PLAN_TYPE,
      });

      return {
        routeEdit: routesWithVolume.find(
          (route) => route.routeCode === routeCode
        ),
        overrideAreas: newOverrideAreas,
        routes: routesWithVolume,
        routesByOuter,
        routesTimes,
        cutOffTimes,
        volumeData: processedVolumeData,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_PROCESSING_EDIT,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const clearRoutePickupAreas = createAsyncThunk(
  "matrixProfile/clearRoutePickupAreas",
  async ({ routeCode }, { dispatch, getState }) => {
    try {
      const state = getState();
      const lookupTables = lookupTableSelector(state);
      const pickupPartners = pickupPartnersSelector(state);
      const volumeData = volumeDataSelector(state);
      const routes = routesSelector(state);
      const routeConfigEdit = routeConfigEditSelector(state);
      const redirectConfig = redirectConfigSelector(state);
      const overrideAreas = overrideAreasSelector(state);
      const pickupAreas = pickupAreasSelector(state);
      const locationConfig = locationConfigSelector(state);

      let updatedPickupAreas = pickupAreas;
      const getRoutePartnerTypeAllocations = ({
        partnerType,
        level,
        routeCode,
      }) =>
        Object.entries(updatedPickupAreas[level]).reduce(
          (acc, [postcode, config]) => {
            if (config[partnerType] === routeCode) acc.push(postcode);
            return acc;
          },
          []
        );
      await asyncLoop(pickupPartners, "forEach", ({ partnerType }) => {
        [OUTER, SECTOR, POSTCODE].forEach((level) => {
          const allocatedPostcodes = getRoutePartnerTypeAllocations({
            partnerType,
            level,
            routeCode,
          });
          updatedPickupAreas = updatePickupAreas({
            routeCode,
            lookupTables,
            selectedPostcodes: allocatedPostcodes,
            pickupAreas: updatedPickupAreas,
            pickupUpdates: [{ key: partnerType, type: REMOVE_TO_NONE }],
          });
        });
      });

      const { routesWithVolume, processedVolumeData } =
        await processRoutesProfile({
          routeConfig: routeConfigEdit,
          redirectConfig,
          routes: routes.map((route) =>
            route.routeCode === routeCode
              ? { ...route, pickupAllocated: false }
              : route
          ),
          volumeData,
          overrideAreas,
          pickupAreas: updatedPickupAreas,
          locationConfig,
          lookupTables,
          planType: PICKUP_PLAN_TYPE,
        });

      return {
        routeEdit: routesWithVolume.find(
          (route) => route.routeCode === routeCode
        ),
        routes: routesWithVolume,
        pickupAreas: updatedPickupAreas,
        volumeData: processedVolumeData,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_PROCESSING_EDIT,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

// SAVE
export const savePlan = createAsyncThunk(
  "matrixProfile/saveProfile",
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    const isDailyPlan = isDailyPlanSelector(state);
    const isProfileValid = isProfileValidSelector(state);
    const type = getMatrixProfileType(state);
    const profile = getSelectedMatrixProfile(state);
    const selectedDepot = getSelectedDepot(state);
    const routeMatrix = getRouteMatrix(state);
    const editRoutes = editRoutesSelector(state);
    const drivers = driversSelector(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const checksum = matrixProfileChecksumSelector(state);
    const depotChecksum = depotChecksumSelector(state);
    const routesModified = routesModifiedSelector(state);
    const routeDayLocks = routeDayLocksSelector(state);

    if (isDailyPlan && !isProfileValid) {
      dispatch(
        addNotification({
          message: ERROR_PLAN_NOT_VALIDATED,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(ERROR_PLAN_NOT_VALIDATED);
    }

    try {
      const { data: saveResponse } = await api.post(
        SAVE_MATRIX_TEMPLATE(
          selectedDepot,
          routeMatrix.matrixProfile.templateId,
          type
        ),
        null,
        {
          driverConfig: Object.values(drivers),
          routeConfig: DELIVERY_PLAN_TYPES.includes(type)
            ? generateRouteConfigFromRoutes({
                routes: editRoutes,
                type,
              })
            : null,
          collectionConfig:
            type === COLLECTIONS_PLAN_TYPE
              ? generateCollectionAreaConfigFromAreas({
                  collectionAreas: collectionAreasSelector(state),
                })
              : null,
          pickupConfig:
            type === PICKUP_PLAN_TYPE ? pickupAreasSelector(state) : null,
          overrideConfig:
            type === OVERRIDES_PLAN_TYPE ? overrideAreasSelector(state) : null,
          locationConfig:
            type === LOCATIONS_PLAN_TYPE ? locationConfigSelector(state) : null,
          sessionChecksum,
          checksum,
          depotChecksum,
          routeDays: isDailyPlan
            ? generateRouteDaysConfig({
                routes: Object.keys(routesModified).map((routeCode) => ({
                  routeCode,
                })),
                routeDayLocks,
              })
            : null,
        }
      );

      saveTemplateChecksum(
        routeMatrix.matrixProfile.templateId,
        saveResponse.matrixProfile.checksum
      );

      dispatch(
        addNotification({
          message: SUCCESS_PLAN_SAVED,
          options: { variant: "success" },
        })
      );

      const changedRoutes = new Set();
      if (isDailyPlan) {
        const newRouteDayLocks = Object.keys(saveResponse.routeDayLocks).reduce(
          (acc, routeCode) => {
            const {
              checksum,
              lockedByUserCode,
              lockedByUserName,
              lockedDatetime,
            } = saveResponse.routeDayLocks[routeCode];
            acc[routeCode] = {
              checksum,
              lockedByUserCode,
              lockedByUserName,
              lockedDatetime,
              lockedByYou: true,
              lockedInDifferentBrowser: false,
            };
            saveRouteDayChecksum({
              dayOfWeek: saveResponse.matrixProfile.day,
              depotCode: selectedDepot,
              routeCode,
              checksum,
            });
            return acc;
          },
          { ...routeDayLocks }
        );
        await dispatch(setRouteDayLocks(newRouteDayLocks));
        await Promise.allSettled(
          Object.entries(routeDayLocks)
            .filter(
              ([routeCode, { lockedByYou, lockedInDifferentBrowser }]) =>
                lockedByYou && !lockedInDifferentBrowser
            )
            .map(async ([routeCode]) => {
              const { error, meta } = await dispatch(unlockRouteDay(routeCode));
              if (error && meta.statusCode === ROUTE_DAY_CHANGED)
                changedRoutes.add(routeCode);
              if (error) throw error;
            })
        );
      }

      const { data: unlockResponse } = await api.post(
        POST_UNLOCK_TEMPLATE(
          selectedDepot,
          routeMatrix.matrixProfile.templateId
        ),
        null,
        {
          sessionChecksum,
          checksum: saveResponse.matrixProfile.checksum,
          depotChecksum,
          forceUnlock: false,
        }
      );
      if (!unlockResponse.success) throw new Error(PLAN_FAILED_TO_UNLOCK);
      deleteTemplateChecksum(saveResponse.matrixProfile.templateId);
      dispatch(
        addNotification({
          message: SUCCESS_UNLOCK_PLAN,
          options: { variant: "success" },
        })
      );

      if (changedRoutes.size)
        dispatch(
          addNotification({
            message: ROUTE_DAY_HAS_CHANGED({
              codes: Array.from(changedRoutes),
            }),
            options: { variant: "error" },
          })
        );

      dispatch(setSelectedRoute(null));
      dispatch(setCollectionTimeEdit(false));
      await dispatch(fetchMatrixProfiles(selectedDepot));
      return {
        planValidated: saveResponse.planValidated,
        matrixProfile: {
          ...saveResponse.matrixProfile,
          lockedByUserCode: null,
          lockedByUserName: null,
          lockedDatetime: null,
          lockedByYou: false,
          lockedInDifferentBrowser: false,
        },
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message || ERROR_SAVING_PLAN,
          options: { variant: "error" },
        })
      );
      if (error.statusCode === DEPOT_MAP_CHANGED)
        await dispatch(fetchLookUpTables(selectedDepot));
      if (
        [DEPOT_MAP_CHANGED, MATRIX_TEMPLATE_CHANGED].includes(error.statusCode)
      )
        await dispatch(fetchDepotRouteMatrix({ type, profile }));
      else if (error.statusCode === MATRIX_TEMPLATE_USER_SESSION_CHANGED)
        dispatch(setPlanManagementExpiredModalOpen(true));
      throw error;
    }
  }
);

export const validateProfile = createAsyncThunk(
  "matrixProfile/validateProfile",
  async (params, { getState }) => {
    const state = getState();
    const planType = getMatrixProfileType(state);
    const driverlessRoutes = driverlessRoutesSelector(state);
    const overrideAreas = overrideAreasSelector(state);
    const { unallocatedAreas = [], singleWaveAreas = [] } =
      planType === DEFAULT_PLAN_TYPE ? areasWaveSplitSelector(state) : {};
    const isValid = isProfileValidSelector(state);

    return isPlanTypeValid({
      planType,
      isValid,
      driverlessRoutes,
      overrideAreas,
      unallocatedAreas,
      singleWaveAreas,
    });
  }
);

// MATRIX PROFILE SELECTION
export const setMatrixProfileOptions = createAsyncThunk(
  "matrixProfile/setOptions",
  async (action, { getState, dispatch }) => {
    if (!action) return;
    const state = getState();
    const type = getMatrixProfileType(state);
    const profile = getSelectedMatrixProfile(state);

    let message = null;
    if (type === null && action.key !== "type") {
      message = MATRIX_TYPE_MISSING;
    } else if (profile === null && action.key !== "profile") {
      message = MATRIX_DAY_MISSING;
    }

    if (message) dispatch(setLoading(false));
    else {
      let params = {
        type,
        profile,
      };

      params[action.key] = action.value;
      dispatch(fetchDepotRouteMatrix(params));
    }
    return { ...action, message };
  }
);

// FETCH DATA
export const fetchMatrixProfiles = createAsyncThunk(
  "matrixProfile/fetchMatrixProfiles",
  async (depotCode, { dispatch }) => {
    try {
      const {
        data: { profiles },
      } = await api.get(GET_MATRIX_PROFILES(depotCode));
      dispatch(
        addNotification({
          message: SUCCESS_FETCH_PROFILES,
          options: { variant: "success" },
        })
      );
      return profiles;
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_PROFILES,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const fetchDepotRouteMatrix = createAsyncThunk(
  "matrixProfile/fetchMatrixTemplate",
  async (params, { getState, dispatch }) => {
    try {
      const { type: planType, profile } = params;
      const state = getState();
      const { dayProfiles, datedProfiles, templates } =
        matrixProfileSelector(state);
      const selectedDepot = getSelectedDepot(state);
      const lookupTables = lookupTableSelector(state);
      const userMetadata = getUserMetadata(state);
      let volumeData = volumeDataSelector(state);

      let templateId = profile.templateId;

      if (!templateId) {
        const matrixProfile = findProfile({
          selectedProfile: profile,
          dayProfiles,
          datedProfiles,
          templates,
        });

        templateId = matrixProfile.templateId;
      }

      const { data } = await api.get(
        GET_MATRIX_TEMPLATE(selectedDepot, templateId, planType)
      );

      let templateDate, templateDay;
      if (data.matrixProfile.day !== null) {
        templateDate = moment()
          .set("day", data.matrixProfile.day)
          .format("YYYY-MM-DD");
        templateDay = data.matrixProfile.day;
      } else if (data.matrixProfile.activeDate) {
        templateDate = moment(
          data.matrixProfile.activeDate,
          "YYYY-MM-DD"
        ).format("YYYY-MM-DD");
        templateDay = moment(templateDate).day();
      }

      const {
        data: { section: sections },
      } = templateDay
        ? await api.get(GET_DAY_SECTIONS(selectedDepot, templateDay))
        : { data: { sections: {} } };

      const {
        data: { vehicles },
      } = await api.get(GET_VEHILCES());

      const {
        data: { pickupPartners },
      } = await api.get(GET_PICKUP_PARTNERS());

      let {
        data: {
          routes,
          routeDayLocks,
          drivers,
          driverProfileLocks,
          driverDayLocks,
        },
      } = await api.get(GET_MATRIX_DAY(selectedDepot, templateDay, templateId));

      let {
        data: { depotCollections },
      } = templateDate
        ? await api.get(
            GET_SCHEDULED_COLLECTIONS(
              selectedDepot,
              data.matrixProfile.day === "" || data.matrixProfile.day === null
                ? moment(data.matrixProfile.activeDate).day()
                : data.matrixProfile.day
            )
          )
        : { data: { depotCollections: [] } };

      const routesObject = keyBy(routes, "routeCode");
      each(depotCollections, (collection) => {
        if (routesObject[collection.routeCode]) {
          const scheduleCollection = {
            scheduleCollectRefs: collection.scheduledCollectionRef,
          };
          if (routesObject[collection.routeCode].scheduledCollections) {
            routesObject[collection.routeCode].scheduledCollections.push(
              scheduleCollection
            );
          } else {
            routesObject[collection.routeCode].scheduledCollections = [
              scheduleCollection,
            ];
          }
        }
        const postcode = lookupTables.POSTCODE[collection.collectionPostcode];
        const stopLocation = {
          coordinates: postcode?.center,
          latitude: postcode?.center?.[1],
          longitude: postcode?.center?.[0],
        };
        collection.stopLocation = stopLocation;
      });
      depotCollections = keyBy(depotCollections, "scheduledCollectionRef");

      routes = sortBy(Object.values(routesObject), ["routeCode"]);
      data.matrixProfile.type = planType;
      data.routeConfig.sort(timeBandsSorter);
      data.redirectConfig = getRedirectConfig(data.routeConfig, lookupTables);
      data.drivers = drivers;
      data.driverProfileLocks = driverProfileLocks;
      data.driverDayLocks = driverDayLocks;
      data.routeDayLocks = routeDayLocks;
      data.vehicles = vehicles;
      data.pickupPartners = pickupPartners;
      data.sections = sections;

      if (volumeData) {
        dispatch(setShowOptimiseAll(true));
        const { overlay_date: overlayDate } = volumeData.overlayProfile;
        ({ data: volumeData } = await api.get(
          GET_VOLUME_DATA(selectedDepot, overlayDate)
        ));
      }

      processTemplateLocks({
        userCode: userMetadata.user.user_id,
        matrixProfile: data.matrixProfile,
        driverProfileLocks: data.driverProfileLocks,
        driverDayLocks: data.driverDayLocks,
        routeDayLocks: data.routeDayLocks,
      });

      dispatch(setShowReloadPlanManagement(false));

      let payload;
      switch (planType) {
        case LOCATIONS_PLAN_TYPE:
          payload = await loadLocationsType({
            matrixProfile: data,
            routes,
            volumeData,
            lookupTables,
          });
          break;
        case COLLECTIONS_PLAN_TYPE:
          payload = loadCollectionsType({
            matrixProfile: data,
            routes,
            depotCollections,
            lookupTables,
          });
          break;
        case PICKUP_PLAN_TYPE:
          payload = await loadPickupType({
            matrixProfile: data,
            routes,
            volumeData,
            lookupTables,
          });
          break;
        case OVERRIDES_PLAN_TYPE:
          payload = await loadOverrideType({
            matrixProfile: data,
            routes,
            volumeData,
            lookupTables,
          });
          break;
        default:
          payload = await loadDefaultTypes({
            depotCollections,
            lookupTables,
            matrixProfile: data,
            routes,
            planType,
            volumeData,
          });
          break;
      }
      dispatch(
        addNotification({
          message: SUCCESS_FETCH_PROFILE,
          options: { variant: "success" },
        })
      );
      return payload;
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_PROFILE,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const fetchRobotSuppliers = createAsyncThunk(
  "matrixProfile/fetchRobotSuppliers",
  async (depotCode, { dispatch }) => {
    try {
      const {
        data: { robotSuppliers },
      } = await api.get(GET_ROBOT_SUPPLIERS(depotCode));

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_ROBOT_SUPPLIERS,
          options: { variant: "success" },
        })
      );

      return robotSuppliers;
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_ROBOT_SUPPLIERS,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const fetchOverlays = createAsyncThunk(
  "matrixProfile/fetchOverlays",
  async (depotCode, { dispatch }) => {
    try {
      const { data: overlayVolumes } = await api.get(
        GET_VOLUME_OVERLAYS(depotCode)
      );

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_VOLUME_OVERLAYS,
          options: { variant: "success" },
        })
      );

      return overlayVolumes;
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_VOLUME_OVERLAYS,
          options: { variant: "error" },
        })
      );

      throw error;
    }
  }
);

export const fetchVolumeData = createAsyncThunk(
  "matrixProfile/fetchVolumeData",
  async ({ overlayDate }, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const overlaySimulationPercentage =
      selectedOverlaySimulationPercentageSelector(state);
    const lookupTables = lookupTableSelector(state);
    const { routeMatrix, routeConfigEdit } = matrixProfileSelector(state);
    const overrideAreas = overrideAreasSelector(state);
    const pickupAreas = pickupAreasSelector(state);
    const locationConfig = locationConfigSelector(state);
    const selectedMatrix = routeConfigEdit
      ? { routeConfig: routeConfigEdit }
      : routeMatrix;
    const redirectConfig = redirectConfigSelector(state);
    const planType = getMatrixProfileType(state);
    const momentDate = moment(overlayDate);

    if (!selectedDepot || !momentDate.isValid()) {
      dispatch(
        addNotification({
          message: INVALID_DEPOT_OR_DATE,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(INVALID_DEPOT_OR_DATE);
    }
    try {
      let { data: volumeData } = await api.get(
        GET_VOLUME_DATA(selectedDepot, overlayDate, overlaySimulationPercentage)
      );

      let { routes, editRoutes } = getState().matrixProfile;

      routes = editRoutes || routes;

      const {
        routesWithVolume,
        routesByOuter,
        routesTimes,
        cutOffTimes,
        processedVolumeData,
      } = await processRoutesProfile({
        routeConfig: selectedMatrix?.routeConfig,
        redirectConfig,
        routes: [...routes],
        volumeData,
        overrideAreas,
        pickupAreas,
        locationConfig,
        lookupTables,
        planType,
      });
      dispatch(setShowOptimiseAll(true));
      dispatch(setSelectSimulatedOverlayOpen(false));
      dispatch(
        addNotification({
          message: SUCCESS_FETCH_VOLUME_OVERLAY,
          options: { variant: "success" },
        })
      );
      return {
        routes: routesWithVolume,
        routesByOuter,
        routesTimes,
        cutOffTimes,
        volumeData: processedVolumeData,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_VOLUME_OVERLAY,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSimulationPercentages = createAsyncThunk(
  "depot/getSimulationPercentages",
  async (
    { overlayDate, selectOverlay },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const formattedDate = formatDateToCorrectTimeZone(overlayDate);
      const state = getState();
      const selectedDepot = getSelectedDepot(state);

      const {
        data: { percentages },
      } = await api.get(
        GET_SIMULATION_PERCENTAGES(selectedDepot, formattedDate)
      );

      if (percentages.length === 0) {
        dispatch(setSelectedSimulationPercentage(0));
        selectOverlay(0);
      } else {
        dispatch(setSelectSimulatedOverlayOpen(percentages.length > 0));
      }

      return percentages;
    } catch (e) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_SIMULATION_PERCENTAGES,
          options: { variant: "error" },
        })
      );
      return rejectWithValue(e.message);
    }
  }
);

// COPY, DELETE AND CREATE PLANS
export const copyPlan = createAsyncThunk(
  "matrixProfile/copyPlan",
  async (
    { selectedDays, copyDrivers, forceDriverUnlock },
    { dispatch, getState }
  ) => {
    const state = getState();
    const selectedProfile = getSelectedMatrixProfile(state);
    const { dayProfiles, datedProfiles, templates } = getMatrixProfiles(state);
    const selectedDepot = getSelectedDepot(state);
    const planType = getMatrixProfileType(state);
    const sessionChecksum = matrixProfileSessionChecksumSelector(state);
    const checksum = matrixProfileChecksumSelector(state);
    const depotChecksum = depotChecksumSelector(state);
    const { user } = getUserMetadata(state);

    try {
      const { templateId } = findProfile({
        selectedProfile,
        dayProfiles,
        datedProfiles,
        templates,
      });

      await serialAsyncForEach(selectedDays, async (day) => {
        const { templateId: originalTemplateId } = dayProfiles.find(
          (profile) => parseInt(profile.day, 10) === day.value
        ) || { templateId: null };
        const {
          data: { unallocatedDriversRoutes },
        } = await api.post(COPY_TO_DAILY_PLAN(selectedDepot), null, {
          sourceTemplateId: templateId,
          sourceSessionChecksum: sessionChecksum,
          sourceChecksum: checksum,
          depotChecksum,
          dayNo: day.value,
          userId: user?.directory_user,
          originalTemplateId,
          copyDrivers,
          forceUnlock: !!forceDriverUnlock,
        });

        // Removes all the checksums associated with the overridden template
        if (originalTemplateId) {
          deleteTemplateChecksum(originalTemplateId);
          deleteDepotRouteDayChecksums({
            dayOfWeek: day.value,
            depotCode: selectedDepot,
          });
          deleteDepotDriverDayChecksums({
            dayOfWeek: day.value,
            depotCode: selectedDepot,
          });
        }

        if (unallocatedDriversRoutes.length)
          dispatch(
            addNotification({
              message: NEW_PLAN_UNALLOCATED_DRIVERS_ROUTES({
                dayOfWeek: day.value,
                unallocatedDriversRoutes,
              }),
              options: { variant: "warning" },
            })
          );
      });

      dispatch(
        addNotification({
          message: SUCCESS_COPY_PLAN,
          options: { variant: "success" },
        })
      );

      dispatch(setCopyModalOpen(false));
      await dispatch(fetchMatrixProfiles(selectedDepot));
      dispatch(setLoading(false));
    } catch (error) {
      dispatch(setCopyModalOpen(false));
      // TODO different modal for locked driver profile/driver day
      if ([DRIVER_PROFILE_LOCKED, DRIVER_DAY_LOCKED].includes(error.statusCode))
        dispatch(setDriversLockedError(error));
      else
        dispatch(
          addNotification({
            message: error.message,
            options: { variant: "error" },
          })
        );

      if (error.statusCode === DEPOT_MAP_CHANGED)
        await dispatch(fetchLookUpTables(selectedDepot));
      if ([DEPOT_MAP_CHANGED, RESOURCE_GONE].includes(error.statusCode))
        await dispatch(fetchMatrixProfiles(selectedDepot));
      if (
        [DEPOT_MAP_CHANGED, MATRIX_TEMPLATE_CHANGED].includes(error.statusCode)
      )
        return await dispatch(
          fetchDepotRouteMatrix({ type: planType, profile: selectedProfile })
        );
      else if (error.statusCode === MATRIX_TEMPLATE_USER_SESSION_CHANGED)
        dispatch(setPlanManagementExpiredModalOpen(true));
      throw error;
    }
  }
);

export const createPlan = createAsyncThunk(
  "matrixProfile/createPlan",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const planType = getMatrixProfileType(state);
    const { user } = getUserMetadata(state);
    const selectedPlan = getSelectedMatrixProfile(state);
    const depotChecksum = depotChecksumSelector(state);

    try {
      const {
        date,
        name,
        basedOn,
        sourcePlan,
        driverDay,
        dutyTime,
        amTimeInDepot,
        pmTimeInDepot,
        forceDriverUnlock,
      } = params;
      const offsettedDate = date
        ? formatDateToSQLTimestamp(offsetDate(date))
        : null;
      const dayNo = [0, 1, 2, 3, 4, 5, 6].includes(driverDay)
        ? `${driverDay}`
        : null;

      dispatch(setSelectPlanModalOpen(false));
      dispatch(setCreateNewTemplateModalOpen(false));
      dispatch(setCreateDatedPlanModalOpen(false));

      let newTemplateId, success, unallocatedDriversRoutes;
      if (basedOn === BLANK)
        ({
          data: { newTemplateId, success },
        } = await api.post(CREATE_BLANK_TEMPLATE(selectedDepot), null, {
          userId: user?.directory_user,
          templateName: name,
          dayNo,
          activeDate: offsettedDate,
          dutyTime,
          amTimeInDepot,
          pmTimeInDepot,
          depotChecksum,
          forceUnlock: !!forceDriverUnlock,
        }));
      else
        ({
          data: { newTemplateId, success, unallocatedDriversRoutes },
        } = await api.post(CREATE_FROM_TEMPLATE(selectedDepot), null, {
          userId: user?.directory_user,
          sourceTemplateId: sourcePlan.value.templateId,
          templateName: name,
          dayNo,
          activeDate: offsettedDate,
          dutyTime,
          amTimeInDepot,
          pmTimeInDepot,
          depotChecksum,
          sourceChecksum: sourcePlan.value.checksum,
          forceUnlock: !!forceDriverUnlock,
        }));

      if (!success) throw new Error(ERROR_CREATE_PLAN);
      else if (unallocatedDriversRoutes?.length)
        dispatch(
          addNotification({
            message: NEW_PLAN_UNALLOCATED_DRIVERS_ROUTES({
              dayOfWeek: dayNo,
              unallocatedDriversRoutes,
            }),
            options: { variant: "warning" },
          })
        );

      await dispatch(fetchMatrixProfiles(selectedDepot));
      dispatch(
        setMatrixProfileOptions({
          key: "profile",
          value: {
            type: offsettedDate ? DATED : TEMPLATE,
            value: newTemplateId,
            templateId: newTemplateId,
          },
        })
      );
      dispatch(
        addNotification({
          message: SUCCESS_CREATE_PLAN,
          options: { variant: "success" },
        })
      );
      return {
        templateId: newTemplateId,
      };
    } catch (error) {
      const isPlanSelected = planType && selectedPlan;
      // TODO different modal for locked driver profile/driver day
      if ([DRIVER_PROFILE_LOCKED, DRIVER_DAY_LOCKED].includes(error.statusCode))
        dispatch(setDriversLockedError(error));
      else
        dispatch(
          addNotification({
            message: error.message,
            options: { variant: "error" },
          })
        );

      if (error.statusCode === DEPOT_MAP_CHANGED)
        await dispatch(fetchLookUpTables(selectedDepot));
      if (
        [DEPOT_MAP_CHANGED, MATRIX_TEMPLATE_CHANGED].includes(error.statusCode)
      ) {
        await dispatch(fetchMatrixProfiles(selectedDepot));
        isPlanSelected &&
          (await dispatch(
            fetchDepotRouteMatrix({ type: planType, profile: selectedPlan })
          ));
      } else if (error.statusCode === MATRIX_TEMPLATE_USER_SESSION_CHANGED)
        dispatch(setPlanManagementExpiredModalOpen(true));
      dispatch(setLoading(false));
    }
  }
);

export const editPlan = createAsyncThunk(
  "matrixProfile/editPlan",
  async (values, { getState, dispatch }) => {
    try {
      const { selectedDepot } = getState().ownApp;

      const { name, templateId } = values;

      dispatch(setEditPlanModalOpen(false));

      await api.put(
        UPDATE_MATRIX_TEMPLATE_NAME(selectedDepot, templateId),
        null,
        {
          templateName: name,
        }
      );

      dispatch(fetchMatrixProfiles(selectedDepot));
      dispatch(
        addNotification({
          message: SUCCESS_EDIT_PLAN,
          options: { variant: "success" },
        })
      );

      return {
        templateId,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_EDIT_PLAN,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const deletePlan = createAsyncThunk(
  "matrixProfile/deletePlan",
  async (values, { getState, dispatch }) => {
    try {
      const { selectedDepot } = getState().ownApp;

      const { templateId } = values;

      dispatch(setEditPlanModalOpen(false));

      await api.delete(DELETE_MATRIX_TEMPLATE(selectedDepot, templateId));

      dispatch(fetchMatrixProfiles(selectedDepot));
      dispatch(resetMatrix());
      dispatch(
        addNotification({
          message: SUCCESS_DELETE_PLAN,
          options: { variant: "success" },
        })
      );

      return {
        templateId,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_DELETE_PLAN,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

// WAVES
export const setWaveEditMode = createAsyncThunk(
  "matrixProfile/setWaveEditMode",
  async (waveEditMode, { dispatch, getState }) => {
    const state = getState();
    const routeConfigEdit = routeConfigEditSelector(state);
    const redirectConfig = redirectConfigSelector(state);
    const matrixProfileLock = matrixProfileLockSelector(state);
    const routes = routesSelector(state);

    if (!matrixProfileLock.lockedByYou) {
      const { error } = await dispatch(lockPlan());
      if (error) throw error;
    }

    return {
      routes,
      waveEditMode,
      routeConfigEdit,
      redirectConfig,
    };
  }
);

export const moveWave = createAsyncThunk(
  "matrixProfile/moveWave",
  async (waves, { dispatch, getState }) => {
    const state = getState();
    const routeConfigEdit = routeConfigEditSelector(state);
    const { depotRoutesOptimisedCopy } = matrixProfileSelector(state);
    const planType = getMatrixProfileType(state);
    const routes = routesSelector(state);
    const overrideAreas = overrideAreasSelector(state);
    const pickupAreas = pickupAreasSelector(state);
    const locationConfig = locationConfigSelector(state);
    const volumeData = volumeDataSelector(state);
    const lookupTables = lookupTableSelector(state);

    const { currentWaveTime, newWaveTime } = waves.find(
      ({ currentWaveTime, newWaveTime }) =>
        newWaveTime && currentWaveTime !== newWaveTime
    );

    const {
      routes: newRoutes,
      routeConfig: newRouteConfig,
      redirectConfig: newRedirectConfig,
      waves: newWaves,
      routesByOuter,
      routesTimes,
      cutOffTimes,
      processedVolumeData,
    } = await applyWaveMove({
      currentWaveTime,
      newWaveTime,
      routes,
      routeConfig: routeConfigEdit,
      planType,
      lookupTables,
      volumeData,
      overrideAreas,
      pickupAreas,
      locationConfig,
    });

    depotRoutesOptimisedCopy && dispatch(setShowOptimiseAll(true));
    return {
      editRoutes: newRoutes,
      waves: newWaves,
      routeConfigEdit: newRouteConfig,
      redirectConfig: newRedirectConfig,
      routesByOuter,
      routesTimes,
      cutOffTimes,
      volumeData: processedVolumeData,
    };
  }
);

export const applyWavesChanges = createAsyncThunk(
  "matrixProfile/applyWavesChanges",
  async (waves) => ({
    waves: waves.map((wave) => {
      wave = { ...wave };
      if (wave.newWaveTime) {
        wave.waveTime = wave.newWaveTime;
        delete wave.newWaveTime;
      }
      if (wave.currentWaveTime) delete wave.currentWaveTime;
      return wave;
    }),
  })
);

export const cancelWavesChanges = createAsyncThunk(
  "matrixProfile/cancelWavesChanges",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const routeConfigEditCopy = routeConfigEditCopySelector(state);
    const redirectConfigCopy = redirectConfigCopySelector(state);
    const { depotRoutesOptimisedCopy } = matrixProfileSelector(state);
    const planType = getMatrixProfileType(state);
    const editRoutesCopy = editRoutesCopySelector(state);
    const overrideAreas = overrideAreasSelector(state);
    const pickupAreas = pickupAreasSelector(state);
    const locationConfig = locationConfigSelector(state);
    const volumeData = volumeDataSelector(state);
    const lookupTables = lookupTableSelector(state);

    const {
      routesWithVolume,
      routesByOuter,
      routesTimes,
      cutOffTimes,
      processedVolumeData,
    } = await processRoutesProfile({
      routeConfig: routeConfigEditCopy,
      redirectConfig: redirectConfigCopy,
      routes: [...editRoutesCopy],
      volumeData,
      overrideAreas,
      pickupAreas,
      locationConfig,
      lookupTables,
      planType,
    });

    depotRoutesOptimisedCopy && dispatch(setShowOptimiseAll(false));

    return {
      routeConfigEdit: routeConfigEditCopy,
      redirectConfig: redirectConfigCopy,
      depotRoutesOptimised: depotRoutesOptimisedCopy,
      editRoutes: routesWithVolume,
      routesByOuter,
      routesTimes,
      cutOffTimes,
      volumeData: processedVolumeData,
    };
  }
);

// COLLECTIONS
export const processCollectionEditAction = createAsyncThunk(
  "matrixProfile/processCollectionEditAction",
  async ({ change }, { getState, dispatch }) => {
    const state = getState();
    const routes = routesSelector(state);
    const editRoutesCopy = editRoutesCopySelector(state);
    const collectionAreas = collectionAreasSelector(state);
    const collectionSelectedTab = selectedTabSelector(state);
    const lookupTables = lookupTableSelector(state);
    const routesImpacted = routesImpactedSelector(state);
    const routesByOuter = routesByOuterSelector(state);

    try {
      const {
        routes: newRoutes,
        routesImpacted: newRoutesImpacted,
        collectionAreas: newCollectionAreas,
        routesByOuter: newRoutesByOuter,
      } = applyChangeToCollections({
        routes,
        preEditRoutes: editRoutesCopy,
        routesImpacted,
        routesByOuter,
        collectionAreas,
        collectionSelectedTab,
        lookupTables,
        change,
      });

      return {
        routeEdit: newRoutes.find(
          ({ routeCode }) => routeCode === change.routeEditCode
        ),
        routes: newRoutes,
        collectionAreas: newCollectionAreas,
        routesImpacted: newRoutesImpacted,
        changedOuter: change.outer,
        routesByOuter: newRoutesByOuter,
        action: change,
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_PROCESSING_EDIT,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const editCollectionTimes = createAsyncThunk(
  "matrixProfile/editCollectionTimes",
  (newCollection, { dispatch, getState }) => {
    dispatch(setCollectionEditPostcode(null));
    const state = getState();
    const collectionAreas = collectionAreasSelector(state);
    const depotDefaults = depotDefaultCollectionValuesSelector(state);
    const lookupTables = lookupTableSelector(state);

    const newCollections = handleCollectionTimeEdits({
      newCollection,
      collectionAreas,
      depotDefaults,
      lookupTables,
    });
    return newCollections;
  }
);

// LOCATIONS
export const processLocationEditAction = createAsyncThunk(
  "matrixProfile/processLocationEditAction",
  async ({ locationCode }, { getState }) => {
    const state = getState();
    const lookupTables = lookupTableSelector(state);
    const routeConfigEdit = routeConfigEditSelector(state);
    const routesImpacted = routesImpactedSelector(state);
    const volumeData = volumeDataSelector(state);
    const redirectConfig = redirectConfigSelector(state);
    const overrideAreas = overrideAreasSelector(state);
    const pickupAreas = pickupAreasSelector(state);
    const locationConfig = editLocationConfigSelector(state);
    const routeEdit = routeInEditSelector(state);
    const routes = editRoutesSelector(state);
    const routeEditCopy = editRoutesCopySelector(state).find(
      ({ routeCode }) => routeEdit.routeCode === routeCode
    );

    return await applyLocationChange({
      locationCode,
      routeEdit,
      routeEditCopy,
      routesImpacted,
      routes,
      redirectConfig,
      routeConfigEdit,
      overrideAreas,
      pickupAreas,
      locationConfig,
      lookupTables,
      volumeData,
    });
  }
);

export const setNoCoordinatesLocation = createAsyncThunk(
  "matrixProfile/setNoCoordinatesLocation",
  (locationCode, { getState }) => {
    const state = getState();
    const location = locationSelector(locationCode)(state);

    return location || null;
  }
);

// MISC
export const setRoutePostcodes = createAsyncThunk(
  "matrixProfile/setRoutePostcodes",
  (value, { getState }) => {
    const state = getState();
    const { routePostcodes, postcodeToRoute, multiData, action } =
      state.matrixProfile;
    const lookupTables = lookupTableSelector(state);
    const customizer = (objValue, srcValue) => {
      if (isArray(objValue)) {
        return [...objValue, ...srcValue];
      }
    };
    const changedOuter = value?.changedOuter;

    let newRoutePostcodes;
    if (!changedOuter) {
      newRoutePostcodes = {
        ...DEFAULT_LABEL_CHANGES,
        ...value?.labelChanges,
      };
    } else if ([ADD, ADD_REDIRECT].includes(action.type)) {
      newRoutePostcodes = {
        OUTER: mergeWith(
          { ...routePostcodes.OUTER },
          value?.labelChanges.OUTER,
          customizer
        ),
        SECTOR: mergeWith(
          { ...routePostcodes.SECTOR },
          value?.labelChanges.SECTOR,
          customizer
        ),
        POSTCODE: mergeWith(
          { ...routePostcodes.POSTCODE },
          value?.labelChanges.POSTCODE,
          customizer
        ),
      };
    } else if (REMOVE === action.type) {
      newRoutePostcodes = {
        OUTER:
          action.level === OUTER
            ? {
                ...routePostcodes.OUTER,
                [action.routeCode]: routePostcodes.OUTER[
                  action.routeCode
                ].filter((i) => i !== action.postcode),
              }
            : routePostcodes.OUTER,
        SECTOR:
          action.level === SECTOR
            ? {
                ...routePostcodes.SECTOR,
                [action.routeCode]: routePostcodes.SECTOR[
                  action.routeCode
                ].filter((i) => i !== action.postcode),
              }
            : routePostcodes.SECTOR,
        POSTCODE:
          action.level === POSTCODE
            ? {
                ...routePostcodes.POSTCODE,
                [action.routeCode]: routePostcodes.POSTCODE[
                  action.routeCode
                ].filter((i) => i !== action.postcode),
              }
            : routePostcodes.POSTCODE,
      };
    } else if ([REPLACE, REMOVE_REDIRECT].includes(action.type)) {
      newRoutePostcodes = { ...routePostcodes };
    }

    let newPostcodeToRoute = { OUTER: {}, SECTOR: {}, POSTCODE: {} };

    if (changedOuter) {
      each(keys(LEVEL_TO_POSTCODE_KEY), (level) => {
        each(keys(postcodeToRoute[level]), (key) => {
          const postcodeKey = new Postcode(key);
          if (postcodeKey.toOuter() === changedOuter) return;

          newPostcodeToRoute[level][key] = postcodeToRoute[level][key];
        });
        if (value?.postcodeToRoute) {
          each(keys(value.postcodeToRoute), (key) => {
            newPostcodeToRoute[key] = {
              ...newPostcodeToRoute[key],
              ...value.postcodeToRoute[key],
            };
          });
        }
      });
    } else {
      newPostcodeToRoute = { ...newPostcodeToRoute, ...value?.postcodeToRoute };
    }

    if (!lookupTables || !lookupTables.POSTCODE) {
      return { routePostcodes: newRoutePostcodes };
    }
    const newMultiPostcodes = processMulti(
      changedOuter ? { ...multiData, ...value?.multi } : value?.multi
    );

    return {
      routePostcodes: newRoutePostcodes,
      multiPostcodes: newMultiPostcodes,
      postcodeToRoute: newPostcodeToRoute,
      multiData: changedOuter
        ? { ...multiData, ...value?.multi }
        : value?.multi,
    };
  }
);

const processMulti = (multi) => {
  const multiPostcodes = { OUTER: {}, SECTOR: {} };
  if (!multi) return multiPostcodes;
  each(Object.keys(multi), (outer) => {
    const outerLevel = multi[outer];
    if (outerLevel.routes.size > 1)
      multiPostcodes.OUTER[outer] = [...outerLevel.routes];
    each(Object.keys(outerLevel.sector), (sector) => {
      const sectorLevel = multi[outer].sector[sector];
      if (sectorLevel.routes.size > 1)
        multiPostcodes.SECTOR[sector] = [...sectorLevel.routes];
    });
  });
  return multiPostcodes;
};

export const cancelEdit = createAsyncThunk(
  "matrixProfile/cancelEdit",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const isDailyPlan = isDailyPlanSelector(state);
    const routeEdit = routeInEditSelector(state);
    const routesModifiedCopy = routesModifiedCopySelector(state);

    dispatch(resetSelectedPostcodes());
    dispatch(setSelectedRoute(null));
    if (!isDailyPlan || routesModifiedCopy[routeEdit.routeCode]) return;
    const { error } = await dispatch(unlockRouteDay(routeEdit.routeCode));
    if (error) throw error;
  }
);

export const applyEdit = createAsyncThunk(
  "matrixProfile/applyEdit",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const isDailyPlan = isDailyPlanSelector(state);
    const planType = getMatrixProfileType(state);
    const routeEdit = routeInEditSelector(state);
    const routesImpacted = routesImpactedSelector(state);
    const routesModifiedCopy = routesModifiedCopySelector(state);
    const wasEditImpactful =
      !!Object.keys(routesImpacted).length ||
      [OVERRIDES_PLAN_TYPE, PICKUP_PLAN_TYPE, LOCATIONS_PLAN_TYPE].includes(
        planType
      );

    dispatch(setSelectedRoute(null));
    dispatch(resetSelectedPostcodes());

    if (
      isDailyPlan &&
      !routesModifiedCopy[routeEdit.routeCode] &&
      !wasEditImpactful
    ) {
      const { error } = await dispatch(unlockRouteDay(routeEdit.routeCode));
      if (error) throw error;
    }

    return {
      routesModified: wasEditImpactful
        ? {
            ...routesModifiedCopy,
            [routeEdit.routeCode]: true,
          }
        : { ...routesModifiedCopy },
    };
  }
);

// OPTIMISATION
export const optimiseDepotRoutes = createAsyncThunk(
  "matrixProfile/optimiseDepotRoutes",
  async (params, { getState, dispatch }) => {
    const state = getState();
    const lookupTables = lookupTableSelector(state);
    const { routeMatrix, scheduledCollections } = matrixProfileSelector(state);
    const planType = getMatrixProfileType(state);
    const collectionType = selectedCollectionTabSelector(state);
    const selectedDepot = getSelectedDepot(state);
    const routes = routesSelector(state);
    const drivers = driversSelector(state);
    const vehicles = vehiclesSelector(state);

    dispatch(
      setDeterminateLoading(
        routes.filter((route) =>
          routeHasTerritory(route, planType, collectionType)
        ).length
      )
    );

    const optimisedRoutes = await optimiseRoutes({
      planType,
      collectionType,
      routes,
      drivers,
      vehicles,
      selectedMatrix: routeMatrix,
      selectedDepot,
      scheduledCollections,
      lookupTables,
      onRouteOptimised: (route) =>
        routeHasTerritory(route, planType, collectionType) &&
        dispatch(increaseDeterminateLoading()),
    });

    dispatch(resetDeterminateLoading());
    dispatch(setShowOptimiseAll(false));
    return optimisedRoutes;
  }
);

const optimiseRoutes = async ({
  planType,
  collectionType,
  routes,
  drivers,
  vehicles,
  selectedMatrix,
  selectedDepot,
  scheduledCollections,
  lookupTables,
  onRouteOptimised,
}) => {
  let routeDate =
    selectedMatrix.matrixProfile.day === "" ||
    selectedMatrix.matrixProfile.day === null
      ? moment(selectedMatrix.matrixProfile.activeDate).format("YYYY-MM-DD")
      : moment().day(selectedMatrix.matrixProfile.day).format("YYYY-MM-DD");

  let optResponses = {};

  await Promise.allSettled(
    routes.map(async (route) => {
      const driver = drivers[route.driverCode];
      const vehicle = vehicles[driver?.vehicleType];
      optResponses[route.routeCode] = await optimiseRoute(
        planType,
        collectionType,
        selectedDepot,
        driver,
        vehicle,
        route,
        routeDate,
        scheduledCollections,
        lookupTables
      );
      onRouteOptimised && onRouteOptimised(route); // TODO does that need driver??? vehicle??
    })
  );
  let optimisedRoutes = routes.map((route) => {
    return {
      ...route,
      routeOptimisation: optResponses[route.routeCode],
      oldRouteOptimisation: route.routeOptimisation,
    };
  });

  return optimisedRoutes;
};

export const optimiseRouteEditImpact = createAsyncThunk(
  "matrixProfile/optimiseRouteEditImpact",
  async (impactedRoutes, { dispatch, getState }) => {
    const state = getState();
    const routes = routesSelector(state);
    const drivers = driversSelector(state);
    const vehicles = vehiclesSelector(state);

    const { payload, error } = await dispatch(
      optimiseImpactedRoutes({
        impactedRoutes,
        routes,
        drivers,
        vehicles,
      })
    );

    if (error) throw error;
    else return payload;
  }
);

export const optimiseImpactedRoutes = createAsyncThunk(
  "matrixProfile/optimiseImpactedRoutes",
  async ({ impactedRoutes, routes, drivers, vehicles }, { getState }) => {
    const state = getState();
    const selectedDepot = getSelectedDepot(state);
    const lookupTables = lookupTableSelector(state);
    const planType = getMatrixProfileType(state);
    const collectionType = selectedCollectionTabSelector(state);
    const routesImpacted = routesImpactedSelector(state);
    const scheduledCollections = collectionsSelector(state);
    const { routeMatrix: selectedMatrix } = matrixProfileSelector(state);

    const optimisedRoutes = await optimiseRoutes({
      planType,
      collectionType,
      routes: impactedRoutes,
      drivers,
      vehicles,
      selectedMatrix,
      selectedDepot,
      scheduledCollections,
      lookupTables,
    });

    const newRoutesImpacted = cloneDeep(routesImpacted);
    const optimisedRoutesByCode = {};
    optimisedRoutes.forEach((route) => {
      if (routesImpacted[route.routeCode])
        newRoutesImpacted[route.routeCode].optimised = true;
      optimisedRoutesByCode[route.routeCode] = route;
    });

    const newEditRoutes = routes.map((route) => {
      if (optimisedRoutesByCode[route.routeCode]) {
        return optimisedRoutesByCode[route.routeCode];
      }
      return route;
    });

    return {
      routes: newEditRoutes,
      routesImpacted: newRoutesImpacted,
    };
  }
);

export const setMatrixModeThunk = createAsyncThunk(
  "matrixProfile/setMatrixModeThunk",
  (value) => value
);

// OVERRIDES
export const editPostcodesOverrides = createAsyncThunk(
  "matrixProfile/editPostcodesOverrides",
  (params, { dispatch, getState }) => {
    const state = getState();
    const overridesForPostcodes = overridesForSelectedPostcodesSelector(state);
    dispatch(setEditOverridesModalOpen(true));
    return {
      newOverrides: {},
      editOverrides: overridesForPostcodes.reduce((acc, override) => {
        acc[override.overrideUuid] = override;
        return acc;
      }, {}),
      deletedOverrides: {},
    };
  }
);

export const generateModalNewOverrides = createAsyncThunk(
  "matrixProfile/generateModalNewOverrides",
  (params, { getState }) => {
    const state = getState();
    const selectedPostcodes = selectedPostcodesSelector(state);
    const routeEdit = routeInEditSelector(state);
    const newOverrides = {};
    const level = Postcode.getLevel(selectedPostcodes[0]);
    selectedPostcodes.forEach((selectedPostcode) => {
      const outer = Postcode.toOuter(selectedPostcode);
      const sector = level !== OUTER ? Postcode.toSector(selectedPostcode) : "";
      const postcode =
        level === POSTCODE ? Postcode.toPostcode(selectedPostcode) : "";
      const overrideUuid = uuidv4();
      newOverrides[overrideUuid] = {
        ...DEFAULT_OVERRIDE,
        overrideUuid,
        routeCode: routeEdit.routeCode,
        level,
        outer,
        sector,
        postcode,
      };
    });
    return newOverrides;
  }
);

export const updateModalNewOverrides = createAsyncThunk(
  "matrixProfile/updateModalNewOverrides",
  (newOverride, { getState }) => {
    const editModalOverrides = editModalOverridesSelector(getState());
    return Object.values(editModalOverrides.newOverrides).reduce(
      (acc, override) => {
        const { overrideUuid, outer, sector, postcode } = override;
        acc[overrideUuid] = {
          ...newOverride,
          overrideUuid,
          outer,
          sector,
          postcode,
        };
        return acc;
      },
      {}
    );
  }
);

export const applyOverridesEdit = createAsyncThunk(
  "matrixProfile/applyOverridesEdit",
  async (params, { dispatch, getState }) => {
    dispatch(setEditOverridesModalOpen(false));
    const state = getState();
    const planType = getMatrixProfileType(state);
    const { routeMatrix, routeConfigEdit } = matrixProfileSelector(state);
    const selectedMatrix = routeConfigEdit
      ? { routeConfig: routeConfigEdit }
      : routeMatrix;
    const volumeData = volumeDataSelector(state);
    const lookupTables = lookupTableSelector(state);
    const { newOverrides, editOverrides, deletedOverrides } =
      editModalOverridesSelector(state);
    const routeEdit = routeInEditSelector(state);
    const routes = routesSelector(state);
    const redirectConfig = redirectConfigSelector(state);
    const locationConfig = locationConfigSelector(state);
    const pickupAreas = pickupAreasSelector(state);
    const overrideAreas = overrideAreasSelector(state);
    const updatedOverrideAreas = updateOverrideFromLevel({
      overrideAreas,
      newOverrides,
      editOverrides,
      deletedOverrides,
    });
    const overridesRouteCodesMap =
      getOverridesRouteCodesMap(updatedOverrideAreas);

    const {
      routesWithVolume,
      routesByOuter,
      routesTimes,
      cutOffTimes,
      processedVolumeData,
    } = await processRoutesProfile({
      routeConfig: selectedMatrix.routeConfig,
      redirectConfig,
      routes: routes.map((route) =>
        route.routeCode !== routeEdit.routeCode
          ? route
          : {
              ...route,
              overridesAllocated: !!overridesRouteCodesMap[route.routeCode],
            }
      ),
      volumeData,
      overrideAreas: updatedOverrideAreas,
      pickupAreas,
      locationConfig,
      lookupTables,
      planType,
    });

    return {
      overrideAreas: updatedOverrideAreas,
      routes: routesWithVolume,
      routesByOuter,
      routesTimes,
      cutOffTimes,
      volumeData: processedVolumeData,
    };
  }
);

export const cancelOverridesEdit = createAsyncThunk(
  "matrixProfile/cancelOverridesEdit",
  (params, { dispatch }) => {
    dispatch(setEditOverridesModalOpen(false));
  }
);

// PICKUP
export const editPostcodesPickup = createAsyncThunk(
  "matrixProfile/editPostcodesPickup",
  (params, { dispatch, getState }) => {
    const state = getState();
    const routeInEditCode = routeInEditCodeSelector(state);
    const selectedPostcodes = selectedPostcodesSelector(state);
    const lookupTables = lookupTableSelector(state);
    const pickupPartners = pickupPartnersSelector(state);
    const pickupAreas = pickupAreasSelector(state);

    const pickupToSelectedPostcodesByType = getPickupToSelectedPostcodesByType({
      lookupTables,
      pickupPartners,
      pickupAreas,
      selectedPostcodes,
    });
    const pickupModalConfig = getPickupModalConfig({
      lookupTables,
      pickupPartners,
      pickupAreas,
      routeInEditCode,
      selectedPostcodes,
      pickupToSelectedPostcodesByType,
    });

    dispatch(setEditPickupModalOpen(true));
    return {
      pickupAreas,
      pickupModalConfig,
    };
  }
);

export const updatePickupModal = createAction(
  "matrixProfile/updatePickupModal"
);

export const setModalPickupEditAction = createAsyncThunk(
  "matrixProfile/setModalPickupEditAction",
  ({ key }, { dispatch, getState }) => {
    const state = getState();
    const lookupTables = lookupTableSelector(state);
    const pickupPartners = pickupPartnersSelector(state);
    const routeInEditCode = routeInEditCodeSelector(state);
    const selectedPostcodes = selectedPostcodesSelector(state);
    const editModalPickupAreas = editModalPickupAreasSelector(state);
    const pickupModalConfig = pickupModalConfigSelector(state);

    const updateOptions = getPickupUpdateOptions({
      key,
      pickupModalConfig,
    }).reduce((acc, updateOption) => {
      const updatedState = getUpdatedPickupModal({
        lookupTables,
        pickupPartners,
        routeInEditCode,
        selectedPostcodes,
        pickupAreas: editModalPickupAreas,
        updateOption,
      });
      const { config: optionConfig } = updatedState.pickupModalConfig.find(
        (config) => config.key === key
      );
      if (!isEqual(updatedState.pickupAreas, editModalPickupAreas))
        acc.push({
          ...updateOption,
          ...updatedState,
          optionConfig,
        });
      return acc;
    }, []);

    if (updateOptions.length < 2) {
      dispatch(updatePickupModal(updateOptions[0]));
    } else {
      dispatch(
        setPickupEditOptionsModal({
          open: true,
          options: updateOptions,
        })
      );
    }
  }
);

export const applyPickupEdit = createAsyncThunk(
  "matrixProfile/applyPickupEdit",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const lookupTables = lookupTableSelector(state);
    const volumeData = volumeDataSelector(state);
    const routeInEditCode = routeInEditCodeSelector(state);
    const routes = routesSelector(state);
    const routeConfigEdit = routeConfigEditSelector(state);
    const redirectConfig = redirectConfigSelector(state);
    const overrideAreas = overrideAreasSelector(state);
    const locationConfig = locationConfigSelector(state);
    const editModalPickupAreas = editModalPickupAreasSelector(state);

    const pickupRouteCodesMap = getPickupRouteCodesMap(editModalPickupAreas);
    const { routesWithVolume, processedVolumeData } =
      await processRoutesProfile({
        routeConfig: routeConfigEdit,
        redirectConfig,
        routes: routes.map((route) => {
          if (route.pickupAllocated === !!pickupRouteCodesMap[route.routeCode])
            return route;
          else
            return {
              ...route,
              pickupAllocated: !!pickupRouteCodesMap[route.routeCode],
            };
        }),
        volumeData,
        overrideAreas,
        pickupAreas: editModalPickupAreas,
        locationConfig,
        lookupTables,
        planType: PICKUP_PLAN_TYPE,
      });

    dispatch(setEditPickupModalOpen(false));

    return {
      routeEdit: routesWithVolume.find(
        (route) => route.routeCode === routeInEditCode
      ),
      routes: routesWithVolume,
      pickupAreas: editModalPickupAreas,
      volumeData: processedVolumeData,
    };
  }
);

export const cancelPickupEdit = createAsyncThunk(
  "matrixProfile/cancelPickupEdit",
  (params, { dispatch }) => {
    dispatch(setEditPickupModalOpen(false));
  }
);
